import { DEFAULT_STOREFRONT_COPY } from "../../config/storefrontCopy";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { assetUrl } from "../../api/http";
import { ContentAPI } from "../../api/content";
import { HomeStatItem, HomeStatTone, SiteContent } from "../../types/content";
import { useViewportMatch } from "../../hooks/useViewportMatch";

const defaultContent: SiteContent = {
  siteName: "BeyondPendants",
  siteTagline: "",
  accentColor: "#c9a227",
  logoUrl: "",
  fallbackImageUrl: "",
  supportButtonLabel: "Support",
  supportWelcomeText: "",
  hero: {
    badgeText: "",
    headline: "",
    subheadline: "",
    primaryCtaLabel: "",
    primaryCtaPath: "/customize",
    secondaryCtaLabel: "",
    secondaryCtaPath: "/customize",
    backgroundImageUrl: "",
  },
  homeCallouts: {
    submitIdeaEyebrow: "New Requests",
    submitIdeaTitle: "Submit a design idea",
    submitIdeaDescription:
      "Tell us what should be customizable next and we will prioritize implementation in upcoming releases.",
    readyTitle: "Ready to personalize your next product?",
    readyDescription:
      "Start from any item type and design with real-time previews before placing your order.",
  },
  submitDesignImageUrl: "",
  proudProductsTitle: "",
  proudProductsSubtitle: "",
  homeStats: [],
  giftWrapFee: 0,
  shippingFee: 0,
  normalShippingFee: 0,
  normalShippingMessage: "Estimated delivery in 5-7 days",
  expressShippingFee: 0,
  expressShippingMessage: "Estimated delivery in 2-4 days",
  taxRatePercent: 8,
  pickupLeadTimeHours: 1,
  aboutUs: "",
  contactUs: "",
  contactInfo: {
    email: "",
    phone: "",
    address: "",
  },
  termsOfService: "",
  faq: [],
  socialLinks: {
    facebook: "",
    instagram: "",
    tiktok: "",
  },
  legalLinks: {
    aboutPath: "/about",
    faqPath: "/faq",
    termsPath: "/terms",
    contactPath: "/contact",
  },
  notificationEmails: {
    recipientEmail: "support@beyondpendants.com",
    orderNotificationsEnabled: true,
    chatNotificationsEnabled: true,
  },
  supportAutoReply: {
    enabled: true,
    message:
      "Thanks for reaching out. Our support team is currently away, but we received your message. These links may help while you wait:",
    cooldownMinutes: 30,
    links: [{ label: "View FAQ", url: "/faq" }],
  },
  authFeatures: {
    googleLoginEnabled: true,
    emailLoginEnabled: true,
    smsLoginEnabled: true,
    smsTemporarilyDisabled: false,
    smsDisableReason: "",
    smsDisabledAt: "",
    smsLimitAlertSentAt: "",
    smsAlertEmailsEnabled: true,
    smsCooldownSeconds: 90,
    smsMaxSendsPer24Hours: 20,
    smsMaxSendsPerPhonePer24Hours: 3,
    smsMaxSendsPerIpPerHour: 5,
    smsExceptionPhoneNumbers: [],
  },
  pendantPages: {
    listing: {
      eyebrow: "Pendants",
      title: "Custom picture pendants ready to personalize",
      description:
        "Start with the available pendant styles below and choose the piece you want to order. Orders received by 12:00 PM are worked immediately and ship first thing the next morning. Nearby customers who place an order before 3:00 PM may request same-day mall pickup.",
      actions: [
        { label: "Same Day Ready", path: "/same-day-pendants" },
        { label: "Browse all products", path: "/customize" },
      ],
      serviceStats: [
        { label: "Orders by 12:00 PM", value: "Ship next morning" },
        { label: "Orders by 3:00 PM", value: "Same-day mall pickup" },
      ],
      infoEyebrow: "Next",
      infoTitle: "What happens once you select the pendant",
      infoDescription:
        "Choose the pendant style first, then upload the image and complete the options shown on that product.",
      infoCards: [
        {
          title: "Choose the pendant first",
          body: "Begin with the pendant style you want to order so pricing, engraving options, and available finishes stay accurate.",
        },
        {
          title: "Upload a clean photo",
          body: "Use a sharp, well-lit image with the subject clearly framed for the strongest final result.",
        },
        {
          title: "Fast production window",
          body: "Orders received by 12:00 PM are worked immediately and ship first thing the next morning. Nearby customers can request same-day mall pickup on orders placed before 3:00 PM.",
          linkLabel: "View same-day pendant timing",
          linkPath: "/same-day-pendants",
        },
      ],
      faqTitle: "Picture Pendant FAQ",
      faqItems: [
        {
          question: "When will my picture pendant ship?",
          answer:
            "Orders received by 12:00 PM are worked immediately and ship first thing the next morning.",
        },
        {
          question: "Can I request same-day pickup?",
          answer:
            "Yes. Nearby customers who place an order before 3:00 PM may request same-day mall pickup.",
        },
        {
          question: "What type of photo should I upload?",
          answer:
            "Choose a sharp, well-lit photo with the subject framed clearly and without heavy blur.",
        },
        {
          question: "Do pendant styles support engraving?",
          answer:
            "Many pendant styles include engraving and other finish options on the product page.",
        },
      ],
    },
    rush: {
      eyebrow: "Same Day",
      title: "Rush-ready pendant ordering",
      description:
        "Choose an available pendant below and place the order within the service window. Orders received by 12:00 PM are worked immediately and ship first thing the next morning. Nearby customers who place an order before 3:00 PM may request same-day mall pickup.",
      actions: [
        { label: "Browse picture pendants", path: "/picture-pendants" },
        { label: "Browse all products", path: "/customize" },
      ],
      serviceStats: [
        { label: "Orders by 12:00 PM", value: "Ship next morning" },
        { label: "Orders by 3:00 PM", value: "Same-day mall pickup" },
      ],
      processEyebrow: "Timing",
      processTitle: "How same-day pendant timing works",
      processDescription:
        "The fastest turnaround comes from ordering the pendant you want and staying inside the published daily cutoff times.",
      processSteps: [
        {
          title: "1. Order by 12:00 PM",
          body: "Orders received by 12:00 PM are worked immediately and ship first thing the next morning.",
        },
        {
          title: "2. Order by 3:00 PM for pickup",
          body: "Nearby customers who place an order before 3:00 PM may request same-day mall pickup.",
        },
        {
          title: "3. Choose shipping or mall pickup",
          body: "Select shipping for next-morning dispatch, or if you are nearby, request same-day mall pickup before the 3:00 PM cutoff.",
        },
      ],
      pickupEyebrow: "Pickup",
      pickupTitle: "Same-day mall pickup",
      pickupEmptyText:
        "Nearby customers who place an order before 3:00 PM may request same-day mall pickup. Add the exact pickup location details below when you want them shown on this page.",
      faqTitle: "Same-Day Pendant FAQ",
      faqItems: [
        {
          question: "What happens if I order before 12:00 PM?",
          answer:
            "Orders received by 12:00 PM are worked immediately and ship first thing the next morning.",
        },
        {
          question: "Can I pick up the pendant the same day?",
          answer:
            "Yes. Nearby customers who place an order before 3:00 PM may request same-day mall pickup.",
        },
        {
          question:
            "Do I need to choose the pendant before requesting rush service?",
          answer:
            "Yes. Choose the pendant first so the order can move directly into production without delay.",
        },
        {
          question: "Does every rush order qualify for mall pickup?",
          answer:
            "Mall pickup is intended for nearby customers who place the order before 3:00 PM. Use the listed pickup details when they are shown on the page.",
        },
      ],
    },
  },
  navCustomizationGroups: [],
  galleryImages: [],
  pickupLocations: [],
};

const DEFAULT_ACCENT_COLOR = "#c9a227";

const normalizeAccentColor = (value: string | undefined) => {
  const raw = String(value || "").trim();
  const normalized = raw.startsWith("#") ? raw : `#${raw}`;
  return /^#[0-9a-fA-F]{6}$/.test(normalized)
    ? normalized.toLowerCase()
    : DEFAULT_ACCENT_COLOR;
};

type RowProps = {
  label: string;
  children: React.ReactNode;
  hint?: string;
};

function Row({ label, hint, children }: RowProps) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      {children}
      {hint ? (
        <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.4 }}>
          {hint}
        </div>
      ) : null}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  fontWeight: 400,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  background: "#ffffff",
};

const textAreaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 112,
  resize: "vertical",
  fontFamily: "inherit",
  lineHeight: 1.5,
};

const richEditorWrapStyle: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: 10,
  overflow: "hidden",
  background: "#fff",
};

const richToolbarStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  borderBottom: "1px solid #e5e7eb",
  padding: "8px 10px",
  background: "#f8fafc",
};

const richBtnStyle: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: 8,
  padding: "4px 8px",
  background: "#fff",
  fontSize: 12,
  cursor: "pointer",
};

const richEditorStyle: React.CSSProperties = {
  minHeight: 88,
  padding: "10px 12px",
  outline: "none",
  fontWeight: 400,
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
  fontSize: 14,
};

const WEEKDAY_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const HOME_STAT_TONE_OPTIONS: Array<{
  value: HomeStatTone;
  label: string;
  preview: string;
}> = [
  { value: "cyan", label: "Cyan", preview: "#06b6d4" },
  { value: "indigo", label: "Indigo", preview: "#6366f1" },
  { value: "emerald", label: "Emerald", preview: "#10b981" },
  { value: "amber", label: "Amber", preview: "#f59e0b" },
  { value: "rose", label: "Rose", preview: "#f43f5e" },
  { value: "slate", label: "Slate", preview: "#64748b" },
];

const SECTION_CARD_STYLE: React.CSSProperties = {
  background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
  border: "1px solid #dbe4f0",
  borderRadius: 22,
  padding: 18,
  display: "grid",
  gap: 14,
  boxShadow: "0 18px 34px rgba(15,23,42,.06)",
};

const SECTION_HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
  paddingBottom: 10,
  borderBottom: "1px solid #eef2f7",
};

const SECTION_TITLE_WRAP_STYLE: React.CSSProperties = {
  display: "grid",
  gap: 4,
};

const SECTION_DESCRIPTION_STYLE: React.CSSProperties = {
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.5,
};

const SECTION_FOOTER_STYLE: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  paddingTop: 12,
  borderTop: "1px solid #e8eef5",
};

type SettingsMode = "content" | "operations";
type ManagedSectionId =
  | "branding"
  | "notifications"
  | "auth-safety"
  | "hero"
  | "home-callouts"
  | "home-stats"
  | "pricing"
  | "pendant-pages"
  | "legal"
  | "faq"
  | "social"
  | "customize-nav"
  | "gallery"
  | "pickup";

const CONTENT_SECTIONS: Array<{ id: ManagedSectionId; label: string }> = [
  { id: "branding", label: "Branding" },
  { id: "hero", label: "Hero" },
  { id: "home-callouts", label: "Home Callouts" },
  { id: "home-stats", label: "Home Stats" },
  { id: "pendant-pages", label: "Pendant Pages" },
  { id: "legal", label: "Legal" },
  { id: "faq", label: "FAQ" },
  { id: "social", label: "Social + Routes" },
  { id: "customize-nav", label: "Customize Nav" },
  { id: "gallery", label: "Gallery" },
];

const OPERATIONS_SECTIONS: Array<{ id: ManagedSectionId; label: string }> = [
  { id: "notifications", label: "Notifications" },
  { id: "auth-safety", label: "Auth + Safety" },
  { id: "pricing", label: "Pricing + Fees" },
  { id: "pickup", label: "Pickup" },
];

function getSectionFragment(
  sectionId: ManagedSectionId,
  content: SiteContent,
): Partial<SiteContent> {
  switch (sectionId) {
    case "branding":
      return {
        siteName: content.siteName,
        siteTagline: content.siteTagline,
        accentColor: content.accentColor,
        logoUrl: content.logoUrl,
        fallbackImageUrl: content.fallbackImageUrl,
        supportButtonLabel: content.supportButtonLabel,
        supportWelcomeText: content.supportWelcomeText,
      };
    case "notifications":
      return {
        notificationEmails: content.notificationEmails,
        supportAutoReply: content.supportAutoReply,
      };
    case "auth-safety":
      return {
        authFeatures: content.authFeatures,
      };
    case "hero":
      return {
        hero: content.hero,
        storefrontCopy: content.storefrontCopy || {},
        submitDesignImageUrl: content.submitDesignImageUrl,
      };
    case "home-callouts":
      return {
        homeCallouts: content.homeCallouts,
      };
    case "home-stats":
      return {
        proudProductsTitle: content.proudProductsTitle,
        proudProductsSubtitle: content.proudProductsSubtitle,
        homeStats: content.homeStats,
      };
    case "pricing":
      return {
        giftWrapFee: Number(content.giftWrapFee || 0),
        shippingFee: Number(content.normalShippingFee ?? content.shippingFee ?? 0),
        normalShippingFee: Number(content.normalShippingFee ?? content.shippingFee ?? 0),
        normalShippingMessage: content.normalShippingMessage || "",
        expressShippingFee: Number(content.expressShippingFee || 0),
        expressShippingMessage: content.expressShippingMessage || "",
        taxRatePercent: Math.max(0, Number(content.taxRatePercent ?? 8)),
      };
    case "pendant-pages":
      return {
        pendantPages: content.pendantPages,
      };
    case "legal":
      return {
        aboutUs: content.aboutUs,
        contactUs: content.contactUs,
        contactInfo: content.contactInfo,
        termsOfService: content.termsOfService,
      };
    case "faq":
      return {
        faq: content.faq,
      };
    case "social":
      return {
        socialLinks: content.socialLinks,
        legalLinks: content.legalLinks,
      };
    case "customize-nav":
      return {
        navCustomizationGroups: content.navCustomizationGroups,
      };
    case "gallery":
      return {
        galleryImages: content.galleryImages,
      };
    case "pickup":
      return {
        pickupLeadTimeHours: Math.min(
          24,
          Math.max(0, Number(content.pickupLeadTimeHours ?? 1)),
        ),
        pickupLocations: (content.pickupLocations || []).map((location) => ({
          ...location,
          alwaysAvailable: false,
          availableDates: [],
        })),
      };
    default:
      return {};
  }
}

const SAVE_BUTTON_STYLE: React.CSSProperties = {
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#fff",
  borderRadius: 999,
  padding: "10px 14px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: 0.2,
  whiteSpace: "nowrap",
};

const PILL_LINK_STYLE: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  borderRadius: 0,
  padding: "8px 13px",
  fontSize: 12,
  fontWeight: 700,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

const RESPONSIVE_TWO_COLS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 10,
};

const RESPONSIVE_THREE_COLS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 8,
};

const TOGGLE_TILE_GRID_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 10,
};

const TOGGLE_TILE_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid #dbe4f0",
  borderRadius: 14,
  padding: "12px 14px",
  background: "#f8fbff",
  minHeight: 58,
};

const INFO_PANEL_STYLE: React.CSSProperties = {
  border: "1px solid #dbe4f0",
  borderRadius: 16,
  padding: 14,
  background: "#f8fafc",
  display: "grid",
  gap: 8,
};

const HEADER_METRIC_GRID_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
  gap: 10,
  width: "100%",
};

const HEADER_METRIC_CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  background: "#fff",
  borderRadius: 0,
  padding: "12px 14px",
  display: "grid",
  gap: 4,
  minWidth: 0,
};

const IMAGE_PREVIEW_CARD_STYLE: React.CSSProperties = {
  border: "1px solid #dbe4f0",
  borderRadius: 16,
  padding: 12,
  background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
  display: "grid",
  gap: 10,
};

const PICKUP_CARD_SECTION_STYLE: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 12,
  display: "grid",
  gap: 10,
  background: "#fff",
};

const PICKUP_WEEKDAY_ROW_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(110px, 130px) minmax(0, 1fr)",
  gap: 12,
  alignItems: "start",
  border: "1px solid #dbe4f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
};

const TIME_SLOT_PATTERN =
  /^([01]\d|2[0-3]):([0-5]\d)\s*-\s*([01]\d|2[0-3]):([0-5]\d)$/;
const DEFAULT_TIME_SLOT = "11:00-19:00";

const parseIsoDate = (value: string): Date | null => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
};

const sanitizeWeekdayList = (value: unknown) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((entry) => Number(entry))
        .filter((entry) => Number.isFinite(entry) && entry >= 0 && entry <= 6)
        .map((entry) => Math.trunc(entry)),
    ),
  ).sort((a, b) => a - b);

const sanitizeTimeSlotList = (value: unknown) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

const sanitizeExceptionPhoneList = (value: unknown) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : String(value || "").split(/[\n,]+/g))
        .map((entry) => String(entry || "").trim())
        .filter(Boolean),
    ),
  ).slice(0, 100);

const normalizeWeekdayTimeSlots = (
  value: unknown,
): Array<{ weekday: number; timeSlots: string[] }> => {
  const rows = Array.isArray(value) ? value : [];
  const merged = new Map<number, string[]>();
  rows.forEach((row) => {
    const weekday = Number((row as any)?.weekday);
    if (!Number.isFinite(weekday) || weekday < 0 || weekday > 6) return;
    const timeSlots = sanitizeTimeSlotList((row as any)?.timeSlots).slice(0, 1);
    if (!timeSlots.length) return;
    merged.set(Math.trunc(weekday), timeSlots);
  });
  return Array.from(merged.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([weekday, timeSlots]) => ({ weekday, timeSlots }));
};

const normalizeIsoDateList = (value: unknown) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((entry) => String(entry || "").trim())
        .filter((entry) => !!parseIsoDate(entry)),
    ),
  ).sort((a, b) => a.localeCompare(b));

const parseIsoDateTextarea = (value: string) =>
  normalizeIsoDateList(
    String(value || "")
      .split(/[\n,]+/g)
      .map((entry) => entry.trim()),
  );

const formatIsoDateTextarea = (value: unknown) =>
  normalizeIsoDateList(value).join("\n");

const parseTimeSlot = (value: string) => {
  const match = String(value || "")
    .trim()
    .match(TIME_SLOT_PATTERN);
  if (!match) {
    return { start: "", end: "" };
  }
  return {
    start: `${match[1]}:${match[2]}`,
    end: `${match[3]}:${match[4]}`,
  };
};

const buildTimeSlot = (start: string, end: string) =>
  start && end ? `${start}-${end}` : "";

function ImagePreview({
  label,
  url,
  height = 180,
}: {
  label: string;
  url?: string;
  height?: number;
}) {
  const resolvedUrl = url ? assetUrl(url) || url : "";
  return (
    <div style={IMAGE_PREVIEW_CARD_STYLE}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13 }}>{label}</div>
        <div style={{ color: "#64748b", fontSize: 11 }}>
          Preview before saving
        </div>
      </div>
      {resolvedUrl ? (
        <img
          src={resolvedUrl}
          alt={label}
          style={{
            width: "100%",
            height,
            objectFit: "contain",
            borderRadius: 12,
            border: "1px solid #dbe4f0",
            background: "#fff",
          }}
        />
      ) : (
        <div
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            minHeight: height,
            display: "grid",
            placeItems: "center",
            color: "#64748b",
            fontSize: 13,
            background: "#fff",
          }}
        >
          No image selected yet.
        </div>
      )}
    </div>
  );
}

function SingleTimeRangeEditor({
  slot,
  onChange,
  disabled = false,
  emptyLabel,
}: {
  slot?: string;
  onChange: (nextSlot: string[]) => void;
  disabled?: boolean;
  emptyLabel: string;
}) {
  const parsed = parseTimeSlot(slot || "");
  const hasValue = Boolean(parsed.start || parsed.end);

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
      >
        <input
          type="time"
          style={{
            ...inputStyle,
            flex: "1 1 140px",
            minWidth: 0,
            opacity: disabled ? 0.6 : 1,
          }}
          value={parsed.start}
          disabled={disabled}
          onChange={(e) => {
            const nextSlot = buildTimeSlot(e.target.value, parsed.end);
            onChange(nextSlot ? [nextSlot] : []);
          }}
        />
        <span style={{ color: "#64748b", fontSize: 12, textAlign: "center" }}>
          to
        </span>
        <input
          type="time"
          style={{
            ...inputStyle,
            flex: "1 1 140px",
            minWidth: 0,
            opacity: disabled ? 0.6 : 1,
          }}
          value={parsed.end}
          disabled={disabled}
          onChange={(e) => {
            const nextSlot = buildTimeSlot(parsed.start, e.target.value);
            onChange(nextSlot ? [nextSlot] : []);
          }}
        />
      </div>
      {!hasValue ? (
        <div
          style={{
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            padding: "10px 12px",
            color: "#64748b",
            fontSize: 12,
            background: "#fff",
          }}
        >
          {emptyLabel}
        </div>
      ) : null}
    </div>
  );
}

type SectionCardProps = {
  id: string;
  title: string;
  description?: string;
  onSave: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
  saving?: boolean;
  savedAt?: string;
  wide?: boolean;
  alternate?: boolean;
  children: React.ReactNode;
};

function SectionCard({
  id,
  title,
  description,
  onSave,
  saveLabel = "Save Section",
  saveDisabled = false,
  saving = false,
  savedAt,
  wide = false,
  alternate = false,
  children,
}: SectionCardProps) {
  const surface = alternate
    ? {
        card: {
          background: "linear-gradient(180deg, #fffdfa 0%, #f8f5ef 100%)",
          border: "1px solid #e7dfd1",
        },
        headerBorder: "#eee5d8",
        footerBorder: "#eee5d8",
      }
    : {
        card: {
          background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
          border: "1px solid #dbe4f0",
        },
        headerBorder: "#eef2f7",
        footerBorder: "#e8eef5",
      };

  return (
    <section
      id={id}
      style={{
        ...SECTION_CARD_STYLE,
        ...surface.card,
        ...(wide ? { gridColumn: "1 / -1" } : {}),
      }}
    >
      <div
        style={{
          ...SECTION_HEADER_STYLE,
          borderBottom: `1px solid ${surface.headerBorder}`,
        }}
      >
        <div style={SECTION_TITLE_WRAP_STYLE}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{title}</h2>
          {description ? (
            <div style={SECTION_DESCRIPTION_STYLE}>{description}</div>
          ) : null}
          {savedAt ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>
              Saved {savedAt}
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ display: "grid", gap: 12 }}>{children}</div>
      <div
        style={{
          ...SECTION_FOOTER_STYLE,
          borderTop: `1px solid ${surface.footerBorder}`,
        }}
      >
        <button
          type="button"
          onClick={onSave}
          disabled={saving || saveDisabled}
          style={{
            ...SAVE_BUTTON_STYLE,
            opacity: saving || saveDisabled ? 0.72 : 1,
            cursor: saving || saveDisabled ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : saveLabel}
        </button>
      </div>
    </section>
  );
}

function HtmlInput({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [textColor, setTextColor] = useState("#111827");
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const collapsedHeight = Math.max(88, rows * 26);

  const measureOverflow = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const nextOverflowing = editor.scrollHeight > collapsedHeight + 8;
    setOverflowing(nextOverflowing);
    if (!nextOverflowing) {
      setExpanded(false);
    }
  }, [collapsedHeight]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML !== value) {
      editor.innerHTML = value || "";
    }
    const rafId = window.requestAnimationFrame(() => {
      measureOverflow();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [measureOverflow, value]);

  const sync = () => {
    onChange(editorRef.current?.innerHTML || "");
    window.requestAnimationFrame(() => {
      measureOverflow();
    });
  };

  const run = (cmd: string, cmdValue?: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand(cmd, false, cmdValue);
    sync();
  };

  const applyInlineStyle = (style: Partial<CSSStyleDeclaration>) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const selection = window.getSelection();
    const range =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const hasEditorSelection =
      !!range && editor.contains(range.commonAncestorContainer);
    const span = document.createElement("span");
    Object.assign(span.style, style);

    if (!hasEditorSelection || !range || range.collapsed) {
      const fragment = document.createDocumentFragment();
      while (editor.firstChild) {
        fragment.appendChild(editor.firstChild);
      }
      span.appendChild(fragment);
      editor.appendChild(span);
    } else {
      span.appendChild(range.extractContents());
      range.insertNode(span);
      selection?.removeAllRanges();
      const nextRange = document.createRange();
      nextRange.selectNodeContents(span);
      selection?.addRange(nextRange);
    }

    sync();
  };

  const setHeading = (tag: "p" | "h2" | "h3" | "blockquote") => {
    run("formatBlock", `<${tag}>`);
  };

  const addLink = () => {
    const url = window.prompt("Enter URL");
    if (!url) return;
    run("createLink", url);
  };

  return (
    <div style={richEditorWrapStyle}>
      <div style={richToolbarStyle}>
        <button type="button" style={richBtnStyle} onClick={() => run("bold")}>
          Bold
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => run("italic")}
        >
          Italic
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => applyInlineStyle({ textDecoration: "underline" })}
        >
          Underline
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => applyInlineStyle({ textDecoration: "line-through" })}
        >
          Strike
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => applyInlineStyle({ textDecoration: "none" })}
        >
          No deco
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => setHeading("h2")}
        >
          H2
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => setHeading("h3")}
        >
          H3
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => setHeading("p")}
        >
          Paragraph
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => setHeading("blockquote")}
        >
          Quote
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => run("insertUnorderedList")}
        >
          Bullets
        </button>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => run("insertOrderedList")}
        >
          Numbered
        </button>
        <button type="button" style={richBtnStyle} onClick={addLink}>
          Link
        </button>
        <label
          style={{
            ...richBtnStyle,
            display: "inline-flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          Color
          <input
            type="color"
            value={textColor}
            onChange={(e) => {
              setTextColor(e.target.value);
              applyInlineStyle({ color: e.target.value });
            }}
            style={{
              width: 22,
              height: 20,
              border: "none",
              padding: 0,
              background: "transparent",
            }}
          />
        </label>
        <button
          type="button"
          style={richBtnStyle}
          onClick={() => run("removeFormat")}
        >
          Clear
        </button>
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => {
            if (overflowing) {
              setExpanded(true);
            }
            try {
              if (document.queryCommandState("bold")) {
                document.execCommand("bold", false);
              }
            } catch {
              // no-op
            }
          }}
          onInput={sync}
          onBlur={sync}
          style={{
            ...richEditorStyle,
            minHeight: Math.max(66, rows * 22),
            maxHeight: overflowing && !expanded ? collapsedHeight : undefined,
            overflow: overflowing && !expanded ? "hidden" : "auto",
          }}
        />
        {overflowing && !expanded ? (
          <div
            style={{
              position: "absolute",
              insetInline: 0,
              bottom: 0,
              height: 46,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 45%, rgba(255,255,255,1) 100%)",
              pointerEvents: "none",
            }}
          />
        ) : null}
      </div>
      {overflowing ? (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 12px 10px",
          }}
        >
          <button
            type="button"
            style={richBtnStyle}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "See less" : "See more"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SiteSettingsPage({ mode }: { mode: SettingsMode }) {
  const isCompact = useViewportMatch("(max-width: 960px)");
  const [content, setContent] = useState<SiteContent>(defaultContent);
  const [savedContentSnapshot, setSavedContentSnapshot] =
    useState<SiteContent>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [galleryUploadingIndex, setGalleryUploadingIndex] = useState<
    number | null
  >(null);
  const [assetUploadingKey, setAssetUploadingKey] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [assetUploadError, setAssetUploadError] = useState<string | null>(null);
  const [galleryUploadError, setGalleryUploadError] = useState<string | null>(
    null,
  );
  const [savedAtBySection, setSavedAtBySection] = useState<
    Record<string, string>
  >({});

  const mergeContent = (
    base: SiteContent,
    next: Partial<SiteContent>,
  ): SiteContent => ({
    ...base,
    ...next,
    hero: { ...base.hero, ...next.hero },
    storefrontCopy: { ...base.storefrontCopy, ...next.storefrontCopy },
    homeCallouts: { ...base.homeCallouts, ...next.homeCallouts },
    socialLinks: { ...base.socialLinks, ...next.socialLinks },
    legalLinks: { ...base.legalLinks, ...next.legalLinks },
    contactInfo: { ...base.contactInfo, ...next.contactInfo },
    notificationEmails: {
      ...base.notificationEmails,
      ...(next.notificationEmails || {}),
    },
    supportAutoReply: {
      ...base.supportAutoReply,
      ...(next.supportAutoReply || {}),
      links: Array.isArray(next.supportAutoReply?.links)
        ? (next.supportAutoReply?.links ?? base.supportAutoReply.links)
        : base.supportAutoReply.links,
    },
    authFeatures: {
      ...base.authFeatures,
      ...(next.authFeatures || {}),
    },
    pendantPages: {
      listing: {
        ...base.pendantPages.listing,
        ...next.pendantPages?.listing,
        actions: Array.isArray(next.pendantPages?.listing?.actions)
          ? (next.pendantPages?.listing?.actions ??
            base.pendantPages.listing.actions)
          : base.pendantPages.listing.actions,
        serviceStats: Array.isArray(next.pendantPages?.listing?.serviceStats)
          ? (next.pendantPages?.listing?.serviceStats ??
            base.pendantPages.listing.serviceStats)
          : base.pendantPages.listing.serviceStats,
        infoCards: Array.isArray(next.pendantPages?.listing?.infoCards)
          ? (next.pendantPages?.listing?.infoCards ??
            base.pendantPages.listing.infoCards)
          : base.pendantPages.listing.infoCards,
        faqItems: Array.isArray(next.pendantPages?.listing?.faqItems)
          ? (next.pendantPages?.listing?.faqItems ??
            base.pendantPages.listing.faqItems)
          : base.pendantPages.listing.faqItems,
      },
      rush: {
        ...base.pendantPages.rush,
        ...next.pendantPages?.rush,
        actions: Array.isArray(next.pendantPages?.rush?.actions)
          ? (next.pendantPages?.rush?.actions ?? base.pendantPages.rush.actions)
          : base.pendantPages.rush.actions,
        serviceStats: Array.isArray(next.pendantPages?.rush?.serviceStats)
          ? (next.pendantPages?.rush?.serviceStats ??
            base.pendantPages.rush.serviceStats)
          : base.pendantPages.rush.serviceStats,
        processSteps: Array.isArray(next.pendantPages?.rush?.processSteps)
          ? (next.pendantPages?.rush?.processSteps ??
            base.pendantPages.rush.processSteps)
          : base.pendantPages.rush.processSteps,
        faqItems: Array.isArray(next.pendantPages?.rush?.faqItems)
          ? (next.pendantPages?.rush?.faqItems ??
            base.pendantPages.rush.faqItems)
          : base.pendantPages.rush.faqItems,
      },
    },
    faq: Array.isArray(next.faq) ? next.faq : base.faq,
    homeStats: Array.isArray(next.homeStats) ? next.homeStats : base.homeStats,
    navCustomizationGroups: Array.isArray(next.navCustomizationGroups)
      ? next.navCustomizationGroups
      : base.navCustomizationGroups,
    galleryImages: Array.isArray(next.galleryImages)
      ? next.galleryImages
      : base.galleryImages,
    pickupLocations: Array.isArray(next.pickupLocations)
      ? next.pickupLocations.map((location) => ({
          ...location,
          alwaysAvailable: Boolean(location.alwaysAvailable),
          availableDates: normalizeIsoDateList(location.availableDates),
          holidayDates: normalizeIsoDateList(location.holidayDates),
          availableWeekdays: sanitizeWeekdayList(location.availableWeekdays),
          timeSlots: sanitizeTimeSlotList(location.timeSlots).slice(0, 1),
          weekdayTimeSlots: Array.isArray(location.weekdayTimeSlots)
            ? normalizeWeekdayTimeSlots(location.weekdayTimeSlots)
            : [],
        }))
      : base.pickupLocations,
    pickupLeadTimeHours:
      Number.isFinite(Number(next.pickupLeadTimeHours)) &&
      Number(next.pickupLeadTimeHours) >= 0
        ? Math.min(24, Number(next.pickupLeadTimeHours))
        : base.pickupLeadTimeHours,
  });

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await ContentAPI.get();
        if (!on) return;
        const merged = mergeContent(defaultContent, data);
        setContent(merged);
        setSavedContentSnapshot(merged);
      } catch (e: any) {
        if (!on) return;
        setError(
          e?.response?.data?.message || e?.message || "Failed to load content.",
        );
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, []);

  const faq = useMemo(() => content.faq || [], [content.faq]);
  const homeStats = useMemo(() => content.homeStats || [], [content.homeStats]);
  const navGroups = useMemo(
    () => content.navCustomizationGroups || [],
    [content.navCustomizationGroups],
  );
  const pendantPages = useMemo(
    () => content.pendantPages,
    [content.pendantPages],
  );
  const pendantListing = useMemo(() => pendantPages.listing, [pendantPages]);
  const pendantRush = useMemo(() => pendantPages.rush, [pendantPages]);
  const galleryImages = useMemo(
    () => content.galleryImages || [],
    [content.galleryImages],
  );
  const pickupLocations = useMemo(
    () => content.pickupLocations || [],
    [content.pickupLocations],
  );

  const updateField = <K extends keyof SiteContent>(
    key: K,
    value: SiteContent[K],
  ) => setContent((prev) => ({ ...prev, [key]: value }));

  const updatePendantPages = (
    updater: (
      value: SiteContent["pendantPages"],
    ) => SiteContent["pendantPages"],
  ) =>
    setContent((prev) => ({
      ...prev,
      pendantPages: updater(prev.pendantPages),
    }));

  const updateHomeCallouts = (
    updater: (
      value: SiteContent["homeCallouts"],
    ) => SiteContent["homeCallouts"],
  ) =>
    setContent((prev) => ({
      ...prev,
      homeCallouts: updater(prev.homeCallouts),
    }));

  const updatePendantListing = (
    updater: (
      value: SiteContent["pendantPages"]["listing"],
    ) => SiteContent["pendantPages"]["listing"],
  ) =>
    updatePendantPages((prev) => ({
      ...prev,
      listing: updater(prev.listing),
    }));

  const updatePendantRush = (
    updater: (
      value: SiteContent["pendantPages"]["rush"],
    ) => SiteContent["pendantPages"]["rush"],
  ) =>
    updatePendantPages((prev) => ({
      ...prev,
      rush: updater(prev.rush),
    }));

  const updatePickupLocation = (
    idx: number,
    updater: (
      location: SiteContent["pickupLocations"][number],
    ) => SiteContent["pickupLocations"][number],
  ) => {
    const next = pickupLocations.map((location, locationIndex) => {
      if (locationIndex !== idx) return location;
      const updated = updater(location);
      return {
        ...updated,
        alwaysAvailable: Boolean(updated.alwaysAvailable),
        availableDates: normalizeIsoDateList(updated.availableDates),
        holidayDates: normalizeIsoDateList(updated.holidayDates),
        availableWeekdays: sanitizeWeekdayList(updated.availableWeekdays),
        timeSlots: sanitizeTimeSlotList(updated.timeSlots).slice(0, 1),
        weekdayTimeSlots: normalizeWeekdayTimeSlots(updated.weekdayTimeSlots),
      };
    });
    updateField("pickupLocations", next);
  };

  const getPickupWeekdaySlots = (
    location: SiteContent["pickupLocations"][number],
    weekday: number,
  ) => {
    const weekdayOverride = normalizeWeekdayTimeSlots(
      location.weekdayTimeSlots,
    ).find((row) => row.weekday === weekday);
    return weekdayOverride?.timeSlots.length
      ? weekdayOverride.timeSlots
      : sanitizeTimeSlotList(location.timeSlots);
  };

  const getPickupScheduleSeedSlots = (
    location: SiteContent["pickupLocations"][number],
  ) => {
    const fallbackSlots = sanitizeTimeSlotList(location.timeSlots).slice(0, 1);
    if (fallbackSlots.length) return fallbackSlots;
    const firstWeekdaySlots = normalizeWeekdayTimeSlots(
      location.weekdayTimeSlots,
    )[0]?.timeSlots;
    return firstWeekdaySlots?.length ? firstWeekdaySlots : [DEFAULT_TIME_SLOT];
  };

  const applyPickupPreset = (
    location: SiteContent["pickupLocations"][number],
    weekdays: number[],
  ) => {
    const nextWeekdays = sanitizeWeekdayList(weekdays);
    if (!nextWeekdays.length) {
      return {
        ...location,
        alwaysAvailable: false,
        availableWeekdays: [],
        timeSlots: [],
        weekdayTimeSlots: [],
      };
    }

    const existingRows = normalizeWeekdayTimeSlots(location.weekdayTimeSlots);
    const existingMap = new Map(
      existingRows.map((row) => [row.weekday, row.timeSlots]),
    );
    const seedSlots = getPickupScheduleSeedSlots(location);

    return {
      ...location,
      alwaysAvailable: false,
      availableWeekdays: nextWeekdays,
      timeSlots: [],
      weekdayTimeSlots: nextWeekdays.map((weekday) => ({
        weekday,
        timeSlots: sanitizeTimeSlotList(existingMap.get(weekday) || seedSlots),
      })),
    };
  };

  const sections =
    mode === "operations" ? OPERATIONS_SECTIONS : CONTENT_SECTIONS;
  const enabledAuthMethodCount = [
    content.authFeatures.googleLoginEnabled,
    content.authFeatures.emailLoginEnabled,
    content.authFeatures.smsLoginEnabled,
  ].filter(Boolean).length;
  const enabledPickupCount = pickupLocations.filter(
    (location) => location.enabled,
  ).length;
  const isAlternateSection = useCallback(
    (sectionId: ManagedSectionId) =>
      Math.max(
        0,
        sections.findIndex((section) => section.id === sectionId),
      ) %
        2 ===
      1,
    [sections],
  );
  const isSectionDirty = useCallback(
    (sectionId: ManagedSectionId) =>
      JSON.stringify(getSectionFragment(sectionId, content)) !==
      JSON.stringify(getSectionFragment(sectionId, savedContentSnapshot)),
    [content, savedContentSnapshot],
  );

  const saveFragment = async (
    sectionKey: ManagedSectionId,
    payload: Partial<SiteContent>,
  ) => {
    try {
      setSavingSection(sectionKey);
      setError(null);
      const updated = await ContentAPI.update(payload);
      const nextContent = mergeContent(content, updated);
      const nextSavedSnapshot = mergeContent(
        savedContentSnapshot,
        getSectionFragment(sectionKey, nextContent),
      );
      setContent(nextContent);
      setSavedContentSnapshot(nextSavedSnapshot);
      setSavedAtBySection((prev) => ({
        ...prev,
        [sectionKey]: new Date().toLocaleString(),
      }));
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to save content.",
      );
    } finally {
      setSavingSection(null);
    }
  };

  const confirmAssetRemoval = (label: string) =>
    window.confirm(
      `Clear ${label}? The old image will be deleted from storage after you save content.`,
    );

  const clearAssetField = (
    key:
      | "logoUrl"
      | "fallbackImageUrl"
      | "hero.backgroundImageUrl"
      | "submitDesignImageUrl",
  ) => {
    if (!confirmAssetRemoval(key)) return;
    setContent((prev) => {
      if (key === "hero.backgroundImageUrl") {
        return {
          ...prev,
          hero: {
            ...prev.hero,
            backgroundImageUrl: "",
          },
        };
      }
      return {
        ...prev,
        [key]: "",
      };
    });
  };

  const uploadAssetField = async (
    key:
      | "logoUrl"
      | "fallbackImageUrl"
      | "hero.backgroundImageUrl"
      | "submitDesignImageUrl",
    file: File,
  ) => {
    try {
      setAssetUploadError(null);
      setAssetUploadingKey(key);
      const uploaded = await ContentAPI.uploadImage(file);
      setContent((prev) => {
        if (key === "hero.backgroundImageUrl") {
          return {
            ...prev,
            hero: {
              ...prev.hero,
              backgroundImageUrl: uploaded.url,
            },
          };
        }
        return {
          ...prev,
          [key]: uploaded.url,
        };
      });
    } catch (e: any) {
      setAssetUploadError(
        e?.response?.data?.message || e?.message || "Failed to upload image.",
      );
    } finally {
      setAssetUploadingKey(null);
    }
  };

  const uploadGalleryImage = async (index: number, file: File) => {
    try {
      setGalleryUploadError(null);
      setGalleryUploadingIndex(index);
      const uploaded = await ContentAPI.uploadImage(file);
      setContent((prev) => {
        const next = [...(prev.galleryImages || [])];
        if (!next[index]) return prev;
        next[index] = { ...next[index], imageUrl: uploaded.url };
        return { ...prev, galleryImages: next };
      });
    } catch (e: any) {
      setGalleryUploadError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to upload gallery image.",
      );
    } finally {
      setGalleryUploadingIndex(null);
    }
  };

  if (loading) {
    return <div style={{ padding: 12 }}>Loading content settings...</div>;
  }

  const pageTitle =
    mode === "operations" ? "Other Settings" : "Content Studio";
  const pageEyebrow = "Content Management";
  const pageDescription =
    mode === "operations"
      ? "Manage pricing, notifications, auth controls, and pickup availability in smaller saveable fragments."
      : "Update branding, homepage copy, navigation, gallery media, and pendant page content in smaller saveable fragments.";
  const headerMetrics =
    mode === "operations"
      ? [
          {
            label: "Sections",
            value: String(sections.length),
            helper: "Save each area independently",
          },
          {
            label: "Pickup Locations",
            value: String(enabledPickupCount),
            helper: "Enabled right now",
          },
          {
            label: "Auth Methods",
            value: String(enabledAuthMethodCount),
            helper: "Login methods enabled",
          },
          {
            label: "Email Alerts",
            value: content.notificationEmails.recipientEmail
              ? "Ready"
              : "Unset",
            helper: "Admin notification destination",
          },
        ]
      : [];

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        maxWidth: 1480,
        margin: "0 auto",
        width: "100%",
        padding: isCompact ? "12px" : "18px",
        paddingBottom: 32,
      }}
    >
      <section
        style={{
          background: "transparent",
          borderRadius: 0,
          padding: 22,
          color: "#0f172a",
          display: "grid",
          gap: 16,
          boxShadow: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {pageEyebrow}
            </div>
            <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>
              {pageTitle}
            </h1>
            <div
              style={{
                color: "#475569",
                fontSize: 14,
                maxWidth: 760,
              }}
            >
              {pageDescription}
            </div>
          </div>
          {headerMetrics.length ? (
            <div
              style={{
                ...HEADER_METRIC_GRID_STYLE,
                maxWidth: isCompact ? "100%" : 420,
                minWidth: isCompact ? 0 : 280,
              }}
            >
              {headerMetrics.map((metric) => (
                <div key={metric.label} style={HEADER_METRIC_CARD_STYLE}>
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {metric.label}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>
                    {metric.value}
                  </div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    {metric.helper}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            position: "sticky",
            top: 12,
            zIndex: 1,
            overflowX: isCompact ? "auto" : "visible",
            paddingBottom: isCompact ? 4 : 0,
          }}
        >
          {sections.map((section) => (
            <a key={section.id} href={`#${section.id}`} style={PILL_LINK_STYLE}>
              {section.label}
            </a>
          ))}
        </div>
      </section>

      {error ? (
        <div
          style={{
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            borderRadius: 10,
            padding: "10px 12px",
          }}
        >
          {error}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 18,
          alignItems: "stretch",
        }}
      >
        {mode === "content" ? (
          <SectionCard
            id="branding"
            title="Branding"
            description="Site identity, fallback media, and support chat copy."
            onSave={() => saveFragment("branding", getSectionFragment("branding", content))}
            saveDisabled={!isSectionDirty("branding")}
            saving={savingSection === "branding"}
            savedAt={savedAtBySection.branding}
            alternate={isAlternateSection("branding")}
          >
          {assetUploadError ? (
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#9f1239",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 12,
              }}
            >
              {assetUploadError}
            </div>
          ) : null}
          <Row
            label="Site Name"
            hint="HTML is supported for styled branding text."
          >
            <HtmlInput
              rows={2}
              value={content.siteName}
              onChange={(next) => updateField("siteName", next)}
            />
          </Row>
          <Row label="Site Tagline" hint="HTML is supported (e.g. bold spans).">
            <HtmlInput
              rows={2}
              value={content.siteTagline}
              onChange={(next) => updateField("siteTagline", next)}
            />
          </Row>
          <Row
            label="Accent Color"
            hint="Used for storefront buttons, focus rings, active nav states, and other branded accents."
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <input
                type="color"
                value={normalizeAccentColor(content.accentColor)}
                onChange={(e) => updateField("accentColor", e.target.value)}
                style={{
                  width: 44,
                  height: 38,
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  background: "#fff",
                  padding: 4,
                  cursor: "pointer",
                }}
                aria-label="Accent color"
              />
              <input
                value={content.accentColor || DEFAULT_ACCENT_COLOR}
                onChange={(e) => updateField("accentColor", e.target.value)}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  const normalized = raw.startsWith("#") ? raw : `#${raw}`;
                  updateField(
                    "accentColor",
                    /^#[0-9a-fA-F]{6}$/.test(normalized)
                      ? normalized.toLowerCase()
                      : DEFAULT_ACCENT_COLOR,
                  );
                }}
                style={{ ...inputStyle, flex: "0 1 150px" }}
                placeholder={DEFAULT_ACCENT_COLOR}
              />
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  border: "1px solid #e2e8f0",
                  borderRadius: 999,
                  background: normalizeAccentColor(content.accentColor),
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                Preview
              </span>
            </div>
          </Row>
          <Row
            label="Logo URL"
            hint="Used in navbar/footer and as global image fallback when product images fail."
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                style={{ ...inputStyle, flex: 1, minWidth: isCompact ? "100%" : 260 }}
                value={content.logoUrl}
                onChange={(e) => updateField("logoUrl", e.target.value)}
              />
              <label
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "7px 10px",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {assetUploadingKey === "logoUrl" ? "Uploading..." : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={assetUploadingKey === "logoUrl"}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    uploadAssetField("logoUrl", file);
                    e.target.value = "";
                  }}
                />
              </label>
              {content.logoUrl ? (
                <button
                  type="button"
                  onClick={() => clearAssetField("logoUrl")}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "7px 10px",
                    background: "#fff1f2",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#be123c",
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </Row>
          <ImagePreview
            label="Logo Preview"
            url={content.logoUrl}
            height={144}
          />
          <Row
            label="Fallback Image URL"
            hint="Optional. If empty, logo is used as fallback image."
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                style={{ ...inputStyle, flex: 1, minWidth: isCompact ? "100%" : 260 }}
                value={content.fallbackImageUrl}
                onChange={(e) =>
                  updateField("fallbackImageUrl", e.target.value)
                }
              />
              <label
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "7px 10px",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {assetUploadingKey === "fallbackImageUrl"
                  ? "Uploading..."
                  : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={assetUploadingKey === "fallbackImageUrl"}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    uploadAssetField("fallbackImageUrl", file);
                    e.target.value = "";
                  }}
                />
              </label>
              {content.fallbackImageUrl ? (
                <button
                  type="button"
                  onClick={() => clearAssetField("fallbackImageUrl")}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "7px 10px",
                    background: "#fff1f2",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#be123c",
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </Row>
          <ImagePreview
            label="Fallback Image Preview"
            url={content.fallbackImageUrl}
            height={144}
          />
          <Row label="Support Button Label" hint="HTML is supported.">
            <HtmlInput
              rows={2}
              value={content.supportButtonLabel}
              onChange={(next) => updateField("supportButtonLabel", next)}
            />
          </Row>
          <Row label="Support Welcome Text" hint="HTML is supported.">
            <HtmlInput
              rows={3}
              value={content.supportWelcomeText}
              onChange={(next) => updateField("supportWelcomeText", next)}
            />
          </Row>
          </SectionCard>
        ) : null}

        {mode === "operations" ? (
          <SectionCard
            id="notifications"
            title="Admin Notification Emails"
            description="Send support emails for new paid orders and customer chat messages, and configure the automatic reply sent when no admin is online."
            onSave={() =>
              saveFragment("notifications", getSectionFragment("notifications", content))
            }
            saveDisabled={!isSectionDirty("notifications")}
            saving={savingSection === "notifications"}
            savedAt={savedAtBySection.notifications}
            alternate={isAlternateSection("notifications")}
          >
          <Row label="Recipient Email">
            <input
              type="email"
              style={inputStyle}
              value={content.notificationEmails.recipientEmail}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  notificationEmails: {
                    ...prev.notificationEmails,
                    recipientEmail: e.target.value,
                  },
                }))
              }
            />
          </Row>
          <div style={TOGGLE_TILE_GRID_STYLE}>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.notificationEmails.orderNotificationsEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    notificationEmails: {
                      ...prev.notificationEmails,
                      orderNotificationsEnabled: e.target.checked,
                    },
                  }))
                }
              />
              New order emails
            </label>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.notificationEmails.chatNotificationsEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    notificationEmails: {
                      ...prev.notificationEmails,
                      chatNotificationsEnabled: e.target.checked,
                    },
                  }))
                }
              />
              New chat message emails
            </label>
          </div>

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: "1px solid #e5e7eb",
              display: "grid",
              gap: 12,
            }}
          >
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                Support Auto Reply
              </div>
              <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
                When no admin panel is currently online, the backend can send a
                one-time automatic support reply with helpful links before a human
                admin has joined the chat.
              </div>
            </div>

            <div style={TOGGLE_TILE_GRID_STYLE}>
              <label style={TOGGLE_TILE_STYLE}>
                <input
                  type="checkbox"
                  checked={!!content.supportAutoReply.enabled}
                  onChange={(e) =>
                    setContent((prev) => ({
                      ...prev,
                      supportAutoReply: {
                        ...prev.supportAutoReply,
                        enabled: e.target.checked,
                      },
                    }))
                  }
                />
                Enable automatic support reply
              </label>
            </div>

            <Row
              label="Reply Message"
              hint="Plain text only. Links below are appended automatically."
            >
              <textarea
                style={{ ...textAreaStyle, minHeight: 120 }}
                value={content.supportAutoReply.message}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    supportAutoReply: {
                      ...prev.supportAutoReply,
                      message: e.target.value,
                    },
                  }))
                }
              />
            </Row>

            <Row
              label="Reply Cooldown (minutes)"
              hint="Prevents repeat auto replies on untouched chats if the customer sends multiple messages while nobody is online."
            >
              <input
                type="number"
                min={1}
                max={1440}
                style={inputStyle}
                value={content.supportAutoReply.cooldownMinutes}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    supportAutoReply: {
                      ...prev.supportAutoReply,
                      cooldownMinutes: Math.max(
                        1,
                        Math.min(1440, Number(e.target.value || 1)),
                      ),
                    },
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                Helpful Links
              </div>
              {(content.supportAutoReply.links || []).map((link, idx) => (
                <div
                  key={`support-auto-reply-link-${idx}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact
                      ? "minmax(0, 1fr)"
                      : "minmax(180px, 1fr) minmax(220px, 1.4fr) auto",
                    gap: 10,
                    alignItems: "end",
                  }}
                >
                  <Row label="Label">
                    <input
                      style={inputStyle}
                      value={link.label}
                      onChange={(e) =>
                        setContent((prev) => {
                          const nextLinks = [...prev.supportAutoReply.links];
                          nextLinks[idx] = {
                            ...nextLinks[idx],
                            label: e.target.value,
                          };
                          return {
                            ...prev,
                            supportAutoReply: {
                              ...prev.supportAutoReply,
                              links: nextLinks,
                            },
                          };
                        })
                      }
                    />
                  </Row>
                  <Row label="URL or Path">
                    <input
                      style={inputStyle}
                      placeholder="/faq or https://example.com/help"
                      value={link.url}
                      onChange={(e) =>
                        setContent((prev) => {
                          const nextLinks = [...prev.supportAutoReply.links];
                          nextLinks[idx] = {
                            ...nextLinks[idx],
                            url: e.target.value,
                          };
                          return {
                            ...prev,
                            supportAutoReply: {
                              ...prev.supportAutoReply,
                              links: nextLinks,
                            },
                          };
                        })
                      }
                    />
                  </Row>
                  <button
                    type="button"
                    onClick={() =>
                      setContent((prev) => ({
                        ...prev,
                        supportAutoReply: {
                          ...prev.supportAutoReply,
                          links: prev.supportAutoReply.links.filter(
                            (_link, linkIndex) => linkIndex !== idx,
                          ),
                        },
                      }))
                    }
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "10px 12px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setContent((prev) => ({
                    ...prev,
                    supportAutoReply: {
                      ...prev.supportAutoReply,
                      links: [
                        ...prev.supportAutoReply.links,
                        { label: "", url: "" },
                      ],
                    },
                  }))
                }
                style={{
                  border: "1px solid #d1d5db",
                  background: "#f8fafc",
                  color: "#111827",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  justifySelf: "start",
                }}
              >
                Add Link
              </button>
            </div>
          </div>
          </SectionCard>
        ) : null}

        {mode === "operations" ? (
          <SectionCard
            id="auth-safety"
            title="Authentication + Safety"
            description="Turn login methods on or off, control app-side OTP rules, and configure unlimited real-SMS test numbers before Firebase sends an OTP."
            onSave={() =>
              saveFragment("auth-safety", getSectionFragment("auth-safety", content))
            }
            saveDisabled={!isSectionDirty("auth-safety")}
            saving={savingSection === "auth-safety"}
            savedAt={savedAtBySection["auth-safety"]}
            alternate={isAlternateSection("auth-safety")}
          >
          <div style={TOGGLE_TILE_GRID_STYLE}>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.authFeatures.googleLoginEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      googleLoginEnabled: e.target.checked,
                    },
                  }))
                }
              />
              Google login enabled
            </label>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.authFeatures.emailLoginEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      emailLoginEnabled: e.target.checked,
                    },
                  }))
                }
              />
              Email login enabled
            </label>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.authFeatures.smsLoginEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsLoginEnabled: e.target.checked,
                    },
                  }))
                }
              />
              SMS login enabled
            </label>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.authFeatures.smsTemporarilyDisabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsTemporarilyDisabled: e.target.checked,
                      smsDisableReason: e.target.checked
                        ? prev.authFeatures.smsDisableReason
                        : "",
                      smsDisabledAt: e.target.checked
                        ? prev.authFeatures.smsDisabledAt ||
                          new Date().toISOString()
                        : "",
                      smsLimitAlertSentAt: e.target.checked
                        ? prev.authFeatures.smsLimitAlertSentAt
                        : "",
                    },
                  }))
                }
              />
              SMS temporarily disabled
            </label>
            <label style={TOGGLE_TILE_STYLE}>
              <input
                type="checkbox"
                checked={!!content.authFeatures.smsAlertEmailsEnabled}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsAlertEmailsEnabled: e.target.checked,
                    },
                  }))
                }
              />
              Email when SMS auto-disables
            </label>
          </div>

          {content.authFeatures.smsTemporarilyDisabled ? (
            <div
              style={{
                ...INFO_PANEL_STYLE,
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#9f1239",
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 700 }}>
                SMS login is currently paused.
              </div>
              <div>
                Reason:{" "}
                {content.authFeatures.smsDisableReason ||
                  "No reason saved yet."}
              </div>
              {content.authFeatures.smsDisabledAt ? (
                <div>
                  Disabled at:{" "}
                  {new Date(
                    content.authFeatures.smsDisabledAt,
                  ).toLocaleString()}
                </div>
              ) : null}
            </div>
          ) : null}

          <div style={RESPONSIVE_TWO_COLS}>
            <Row
              label="SMS cooldown seconds"
              hint="Minimum wait before the same number can request another OTP. Set 0 to disable cooldown."
            >
              <input
                type="number"
                min={0}
                max={3600}
                style={inputStyle}
                value={content.authFeatures.smsCooldownSeconds}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsCooldownSeconds: Number(e.target.value || 0),
                    },
                  }))
                }
              />
            </Row>
            <Row
              label="SMS max sends per 24 hours"
              hint="When this cap is reached, SMS login auto-disables until you re-enable it here. Set 0 to disable this cap."
            >
              <input
                type="number"
                min={0}
                max={5000}
                style={inputStyle}
                value={content.authFeatures.smsMaxSendsPer24Hours}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsMaxSendsPer24Hours: Number(e.target.value || 0),
                    },
                  }))
                }
              />
            </Row>
            <Row
              label="SMS max sends per phone / 24h"
              hint="Extra protection against repeated OTP requests to one number. Set 0 to disable this cap."
            >
              <input
                type="number"
                min={0}
                max={100}
                style={inputStyle}
                value={content.authFeatures.smsMaxSendsPerPhonePer24Hours}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsMaxSendsPerPhonePer24Hours: Number(
                        e.target.value || 0,
                      ),
                    },
                  }))
                }
              />
            </Row>
            <Row
              label="SMS max sends per IP / hour"
              hint="Blocks rapid retries from one network before Firebase is called. Set 0 to disable this cap."
            >
              <input
                type="number"
                min={0}
                max={1000}
                style={inputStyle}
                value={content.authFeatures.smsMaxSendsPerIpPerHour}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    authFeatures: {
                      ...prev.authFeatures,
                      smsMaxSendsPerIpPerHour: Number(e.target.value || 0),
                    },
                  }))
                }
              />
            </Row>
          </div>

          <div style={INFO_PANEL_STYLE}>
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                Unlimited Real-SMS Test Numbers
              </div>
              <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
                These numbers bypass the app-level SMS cooldown and all app-side
                caps so you can keep testing with a real device. Use one number
                per line. Firebase still controls the actual SMS provider and
                can still throttle real messages.
              </div>
            </div>
            <textarea
              style={textAreaStyle}
              placeholder={["+15551234567", "+15557654321"].join("\n")}
              value={(content.authFeatures.smsExceptionPhoneNumbers || []).join(
                "\n",
              )}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  authFeatures: {
                    ...prev.authFeatures,
                    smsExceptionPhoneNumbers: sanitizeExceptionPhoneList(
                      e.target.value,
                    ),
                  },
                }))
              }
            />
            {(content.authFeatures.smsExceptionPhoneNumbers || []).length ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {content.authFeatures.smsExceptionPhoneNumbers.map(
                  (phoneNumber) => (
                    <span
                      key={phoneNumber}
                      style={{
                        border: "1px solid #bfdbfe",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        borderRadius: 999,
                        padding: "7px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {phoneNumber}
                    </span>
                  ),
                )}
              </div>
            ) : (
              <div style={{ color: "#64748b", fontSize: 12 }}>
                No exception numbers added.
              </div>
            )}
          </div>

          <Row
            label="SMS pause reason"
            hint="This is set automatically when SMS login is disabled by the safety cap, but you can edit it manually too."
          >
            <textarea
              style={textAreaStyle}
              value={content.authFeatures.smsDisableReason}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  authFeatures: {
                    ...prev.authFeatures,
                    smsDisableReason: e.target.value,
                  },
                }))
              }
            />
          </Row>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="hero"
            title="Home Hero"
            description="Homepage hero copy, CTAs, and hero artwork."
            onSave={() => saveFragment("hero", getSectionFragment("hero", content))}
            saveDisabled={!isSectionDirty("hero")}
            saving={savingSection === "hero"}
            savedAt={savedAtBySection.hero}
            alternate={isAlternateSection("hero")}
          >
          <details style={{ marginBottom: 24, padding: 16, border: "1px solid #d1d5db", borderRadius: 10 }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>Homepage, ribbons & loader text</summary>
            <p style={{ fontSize: 13, color: "#64748b", marginBlock: 12 }}>Edit every homepage label, announcement and loader message here. The existing Hero fields below take priority for the headline, description and button labels. Blank fields use the shown default; use Save Section to publish your edits.</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
              {Object.entries(DEFAULT_STOREFRONT_COPY).map(([key, fallback]) => <label key={key} style={{ display: "grid", gap: 5, fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{key.replace(/([A-Z])/g, " $1").replace(/^./, char => char.toUpperCase())}</span>
                <textarea rows={2} style={inputStyle} placeholder={fallback} value={content.storefrontCopy?.[key] ?? ""} onChange={event => updateField("storefrontCopy", { ...content.storefrontCopy, [key]: event.target.value || fallback })} />
              </label>)}
            </div>
          </details>
          {assetUploadError ? (
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#9f1239",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 12,
              }}
            >
              {assetUploadError}
            </div>
          ) : null}
          <Row label="Badge Text" hint="HTML is supported.">
            <HtmlInput
              rows={2}
              value={content.hero.badgeText}
              onChange={(next) =>
                setContent((prev) => ({
                  ...prev,
                  hero: { ...prev.hero, badgeText: next },
                }))
              }
            />
          </Row>
          <Row label="Headline" hint="HTML is supported for styled headlines.">
            <HtmlInput
              rows={3}
              value={content.hero.headline}
              onChange={(next) =>
                setContent((prev) => ({
                  ...prev,
                  hero: { ...prev.hero, headline: next },
                }))
              }
            />
          </Row>
          <Row label="Subheadline" hint="HTML is supported.">
            <HtmlInput
              rows={4}
              value={content.hero.subheadline}
              onChange={(next) =>
                setContent((prev) => ({
                  ...prev,
                  hero: { ...prev.hero, subheadline: next },
                }))
              }
            />
          </Row>
          <Row label="Background Image URL">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                style={{ ...inputStyle, flex: 1, minWidth: isCompact ? "100%" : 260 }}
                value={content.hero.backgroundImageUrl}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    hero: { ...prev.hero, backgroundImageUrl: e.target.value },
                  }))
                }
              />
              <label
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "7px 10px",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {assetUploadingKey === "hero.backgroundImageUrl"
                  ? "Uploading..."
                  : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={assetUploadingKey === "hero.backgroundImageUrl"}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    uploadAssetField("hero.backgroundImageUrl", file);
                    e.target.value = "";
                  }}
                />
              </label>
              {content.hero.backgroundImageUrl ? (
                <button
                  type="button"
                  onClick={() => clearAssetField("hero.backgroundImageUrl")}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "7px 10px",
                    background: "#fff1f2",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#be123c",
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </Row>
          <ImagePreview
            label="Hero Background Preview"
            url={content.hero.backgroundImageUrl}
            height={220}
          />
          <Row
            label="Submit A Design Image URL"
            hint="Used in the Submit A Design section on home page."
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <input
                style={{ ...inputStyle, flex: 1, minWidth: isCompact ? "100%" : 260 }}
                value={content.submitDesignImageUrl}
                onChange={(e) =>
                  updateField("submitDesignImageUrl", e.target.value)
                }
              />
              <label
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "7px 10px",
                  background: "#f8fafc",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {assetUploadingKey === "submitDesignImageUrl"
                  ? "Uploading..."
                  : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  disabled={assetUploadingKey === "submitDesignImageUrl"}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    uploadAssetField("submitDesignImageUrl", file);
                    e.target.value = "";
                  }}
                />
              </label>
              {content.submitDesignImageUrl ? (
                <button
                  type="button"
                  onClick={() => clearAssetField("submitDesignImageUrl")}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "7px 10px",
                    background: "#fff1f2",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#be123c",
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </Row>
          <ImagePreview
            label="Submit A Design Preview"
            url={content.submitDesignImageUrl}
            height={220}
          />
          <div style={RESPONSIVE_TWO_COLS}>
            <Row label="Primary CTA Label" hint="HTML is supported.">
              <HtmlInput
                rows={2}
                value={content.hero.primaryCtaLabel}
                onChange={(next) =>
                  setContent((prev) => ({
                    ...prev,
                    hero: { ...prev.hero, primaryCtaLabel: next },
                  }))
                }
              />
            </Row>
            <Row label="Primary CTA Path">
              <input
                style={inputStyle}
                value={content.hero.primaryCtaPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    hero: { ...prev.hero, primaryCtaPath: e.target.value },
                  }))
                }
              />
            </Row>
          </div>
          <div style={RESPONSIVE_TWO_COLS}>
            <Row label="Secondary CTA Label" hint="HTML is supported.">
              <HtmlInput
                rows={2}
                value={content.hero.secondaryCtaLabel}
                onChange={(next) =>
                  setContent((prev) => ({
                    ...prev,
                    hero: { ...prev.hero, secondaryCtaLabel: next },
                  }))
                }
              />
            </Row>
            <Row label="Secondary CTA Path">
              <input
                style={inputStyle}
                value={content.hero.secondaryCtaPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    hero: { ...prev.hero, secondaryCtaPath: e.target.value },
                  }))
                }
              />
            </Row>
          </div>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="home-callouts"
            title="Home Callouts"
            description="Manage the short sections under the hero that invite visitors to submit ideas or start customizing."
            onSave={() =>
              saveFragment("home-callouts", getSectionFragment("home-callouts", content))
            }
            saveDisabled={!isSectionDirty("home-callouts")}
            saving={savingSection === "home-callouts"}
            savedAt={savedAtBySection["home-callouts"]}
            alternate={isAlternateSection("home-callouts")}
          >
            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Submit Idea Eyebrow">
                <input
                  style={inputStyle}
                  value={content.homeCallouts.submitIdeaEyebrow}
                  onChange={(e) =>
                    updateHomeCallouts((prev) => ({
                      ...prev,
                      submitIdeaEyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Submit Idea Title">
                <input
                  style={inputStyle}
                  value={content.homeCallouts.submitIdeaTitle}
                  onChange={(e) =>
                    updateHomeCallouts((prev) => ({
                      ...prev,
                      submitIdeaTitle: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>
            <Row label="Submit Idea Description">
              <textarea
                rows={3}
                style={textAreaStyle}
                value={content.homeCallouts.submitIdeaDescription}
                onChange={(e) =>
                  updateHomeCallouts((prev) => ({
                    ...prev,
                    submitIdeaDescription: e.target.value,
                  }))
                }
              />
            </Row>
            <Row label="Ready Section Title">
              <input
                style={inputStyle}
                value={content.homeCallouts.readyTitle}
                onChange={(e) =>
                  updateHomeCallouts((prev) => ({
                    ...prev,
                    readyTitle: e.target.value,
                  }))
                }
              />
            </Row>
            <Row label="Ready Section Description">
              <textarea
                rows={3}
                style={textAreaStyle}
                value={content.homeCallouts.readyDescription}
                onChange={(e) =>
                  updateHomeCallouts((prev) => ({
                    ...prev,
                    readyDescription: e.target.value,
                  }))
                }
              />
            </Row>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="home-stats"
            title="Home Stats"
            description="Homepage metric cards and the featured products intro copy."
            onSave={() => saveFragment("home-stats", getSectionFragment("home-stats", content))}
            saveDisabled={!isSectionDirty("home-stats")}
            saving={savingSection === "home-stats"}
            savedAt={savedAtBySection["home-stats"]}
            alternate={isAlternateSection("home-stats")}
          >
          <Row label="Products We Are Proud Of Title" hint="HTML is supported.">
            <HtmlInput
              rows={2}
              value={content.proudProductsTitle}
              onChange={(next) => updateField("proudProductsTitle", next)}
            />
          </Row>
          <Row
            label="Products We Are Proud Of Subtitle"
            hint="HTML is supported."
          >
            <HtmlInput
              rows={2}
              value={content.proudProductsSubtitle}
              onChange={(next) => updateField("proudProductsSubtitle", next)}
            />
          </Row>
          <div style={{ display: "grid", gap: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <div style={{ fontWeight: 700 }}>Homepage Middle Stats</div>
                <div style={{ color: "#64748b", fontSize: 12 }}>
                  If left empty, the storefront uses its current computed
                  defaults.
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  updateField("homeStats", [
                    ...homeStats,
                    { label: "", value: "", tone: "cyan" },
                  ])
                }
                style={{
                  border: "1px solid #d1d5db",
                  background: "#f8fafc",
                  color: "#111827",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Add Stat
              </button>
            </div>
            {homeStats.length ? (
              <div style={{ display: "grid", gap: 10 }}>
                {homeStats.map((item: HomeStatItem, idx: number) => (
                  <div
                    key={`home-stat-${idx}`}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: 12,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div style={RESPONSIVE_THREE_COLS}>
                      <Row label="Label">
                        <input
                          style={inputStyle}
                          value={item.label}
                          onChange={(e) => {
                            const next = [...homeStats];
                            next[idx] = { ...next[idx], label: e.target.value };
                            updateField("homeStats", next);
                          }}
                        />
                      </Row>
                      <Row label="Value">
                        <input
                          style={inputStyle}
                          value={item.value}
                          onChange={(e) => {
                            const next = [...homeStats];
                            next[idx] = { ...next[idx], value: e.target.value };
                            updateField("homeStats", next);
                          }}
                        />
                      </Row>
                      <Row label="Color Tone">
                        <select
                          style={inputStyle}
                          value={item.tone}
                          onChange={(e) => {
                            const next = [...homeStats];
                            next[idx] = {
                              ...next[idx],
                              tone: e.target.value as HomeStatTone,
                            };
                            updateField("homeStats", next);
                          }}
                        >
                          {HOME_STAT_TONE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Row>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#64748b",
                          fontSize: 12,
                        }}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 999,
                            background:
                              HOME_STAT_TONE_OPTIONS.find(
                                (option) => option.value === item.tone,
                              )?.preview || "#06b6d4",
                          }}
                        />
                        Preview tone
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          updateField(
                            "homeStats",
                            homeStats.filter(
                              (_row, rowIndex) => rowIndex !== idx,
                            ),
                          )
                        }
                        style={{
                          border: "1px solid #fecaca",
                          background: "#fff1f2",
                          color: "#be123c",
                          borderRadius: 8,
                          padding: "8px 10px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Remove Stat
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 14,
                  padding: 14,
                  color: "#64748b",
                  fontSize: 13,
                  background: "#f8fafc",
                }}
              >
                No custom home stats configured yet.
              </div>
            )}
          </div>
          </SectionCard>
        ) : null}

        {mode === "operations" ? (
          <SectionCard
            id="pricing"
            title="Pricing + Fees"
            description="Global gift-wrap and shipping fees used at cart, checkout, and order creation."
            onSave={() => saveFragment("pricing", getSectionFragment("pricing", content))}
            saveDisabled={!isSectionDirty("pricing")}
            saving={savingSection === "pricing"}
            savedAt={savedAtBySection.pricing}
            alternate={isAlternateSection("pricing")}
          >
            <div style={RESPONSIVE_TWO_COLS}>
              <Row
                label="Gift Wrap Fee (USD)"
                hint="Applied when customer selects gift option on any product."
              >
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  style={inputStyle}
                  value={Number(content.giftWrapFee || 0)}
                  onChange={(e) =>
                    updateField(
                      "giftWrapFee",
                      Math.max(0, Number(e.target.value || 0)),
                    )
                  }
                />
              </Row>
              <Row
                label="Normal Delivery Fee (USD)"
                hint="Standard shipping price used at cart, checkout, and order creation."
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    style={inputStyle}
                    value={Number(content.normalShippingFee ?? content.shippingFee ?? 0)}
                    onChange={(e) => {
                      const fee = Math.max(0, Number(e.target.value || 0));
                      updateField("normalShippingFee", fee);
                      updateField("shippingFee", fee);
                    }}
                  />
                  <input
                    type="text"
                    maxLength={140}
                    style={inputStyle}
                    placeholder="Estimated delivery in 5-7 days"
                    value={content.normalShippingMessage || ""}
                    onChange={(e) =>
                      updateField("normalShippingMessage", e.target.value)
                    }
                  />
                </div>
              </Row>
              <Row
                label="Express Delivery Fee (USD)"
                hint="Higher-attention express option shown during checkout."
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    style={inputStyle}
                    value={Number(content.expressShippingFee || 0)}
                    onChange={(e) =>
                      updateField(
                        "expressShippingFee",
                        Math.max(0, Number(e.target.value || 0)),
                      )
                    }
                  />
                  <input
                    type="text"
                    maxLength={140}
                    style={inputStyle}
                    placeholder="Estimated delivery in 2-4 days"
                    value={content.expressShippingMessage || ""}
                    onChange={(e) =>
                      updateField("expressShippingMessage", e.target.value)
                    }
                  />
                </div>
              </Row>
              <Row
                label="Tax Rate (%)"
                hint="Applied to the discounted item subtotal for all checkout items."
              >
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  style={inputStyle}
                  value={Number(content.taxRatePercent ?? 8)}
                  onChange={(e) =>
                    updateField(
                      "taxRatePercent",
                      Math.min(100, Math.max(0, Number(e.target.value || 0))),
                    )
                  }
                />
              </Row>
            </div>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
          id="pendant-pages"
          title="Pendant Pages + Fulfillment Copy"
          description="Manage all public copy for the picture pendant page and same-day pendant page. Use this section for the 12:00 PM ship-next-morning rule and the 3:00 PM mall pickup rule."
          onSave={() => saveFragment("pendant-pages", getSectionFragment("pendant-pages", content))}
          saveDisabled={!isSectionDirty("pendant-pages")}
          saving={savingSection === "pendant-pages"}
          savedAt={savedAtBySection["pendant-pages"]}
          alternate={isAlternateSection("pendant-pages")}
          wide
        >
          <div style={{ display: "grid", gap: 16 }}>
            <div style={INFO_PANEL_STYLE}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                Picture Pendant Page
              </div>
              <div style={{ color: "#64748b", fontSize: 13 }}>
                This content appears on the main pendant landing page under
                `/picture-pendants`.
              </div>
            </div>

            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Eyebrow">
                <input
                  style={inputStyle}
                  value={pendantListing.eyebrow}
                  onChange={(e) =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      eyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Title">
                <input
                  style={inputStyle}
                  value={pendantListing.title}
                  onChange={(e) =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>

            <Row label="Hero Description">
              <textarea
                rows={4}
                style={textAreaStyle}
                value={pendantListing.description}
                onChange={(e) =>
                  updatePendantListing((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Hero Action Links</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      actions: [...prev.actions, { label: "", path: "" }],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Action
                </button>
              </div>
              {pendantListing.actions.map((item, idx) => (
                <div
                  key={`listing-action-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Label">
                      <input
                        style={inputStyle}
                        value={item.label}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.actions];
                            next[idx] = { ...next[idx], label: e.target.value };
                            return { ...prev, actions: next };
                          })
                        }
                      />
                    </Row>
                    <Row label="Path">
                      <input
                        style={inputStyle}
                        value={item.path}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.actions];
                            next[idx] = { ...next[idx], path: e.target.value };
                            return { ...prev, actions: next };
                          })
                        }
                      />
                    </Row>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantListing((prev) => ({
                        ...prev,
                        actions: prev.actions.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Action
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Service Promise Stats</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      serviceStats: [
                        ...prev.serviceStats,
                        { label: "", value: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Stat
                </button>
              </div>
              {pendantListing.serviceStats.map((item, idx) => (
                <div
                  key={`listing-stat-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Label">
                      <input
                        style={inputStyle}
                        value={item.label}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.serviceStats];
                            next[idx] = { ...next[idx], label: e.target.value };
                            return { ...prev, serviceStats: next };
                          })
                        }
                      />
                    </Row>
                    <Row label="Value">
                      <input
                        style={inputStyle}
                        value={item.value}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.serviceStats];
                            next[idx] = { ...next[idx], value: e.target.value };
                            return { ...prev, serviceStats: next };
                          })
                        }
                      />
                    </Row>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantListing((prev) => ({
                        ...prev,
                        serviceStats: prev.serviceStats.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Stat
                  </button>
                </div>
              ))}
            </div>

            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Support Eyebrow">
                <input
                  style={inputStyle}
                  value={pendantListing.infoEyebrow}
                  onChange={(e) =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      infoEyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Support Title">
                <input
                  style={inputStyle}
                  value={pendantListing.infoTitle}
                  onChange={(e) =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      infoTitle: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>

            <Row label="Support Description">
              <textarea
                rows={3}
                style={textAreaStyle}
                value={pendantListing.infoDescription}
                onChange={(e) =>
                  updatePendantListing((prev) => ({
                    ...prev,
                    infoDescription: e.target.value,
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Support Cards</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      infoCards: [
                        ...prev.infoCards,
                        { title: "", body: "", linkLabel: "", linkPath: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Card
                </button>
              </div>
              {pendantListing.infoCards.map((item, idx) => (
                <div
                  key={`listing-card-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <Row label="Card Title">
                    <input
                      style={inputStyle}
                      value={item.title}
                      onChange={(e) =>
                        updatePendantListing((prev) => {
                          const next = [...prev.infoCards];
                          next[idx] = { ...next[idx], title: e.target.value };
                          return { ...prev, infoCards: next };
                        })
                      }
                    />
                  </Row>
                  <Row label="Card Body">
                    <textarea
                      rows={3}
                      style={textAreaStyle}
                      value={item.body}
                      onChange={(e) =>
                        updatePendantListing((prev) => {
                          const next = [...prev.infoCards];
                          next[idx] = { ...next[idx], body: e.target.value };
                          return { ...prev, infoCards: next };
                        })
                      }
                    />
                  </Row>
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Link Label">
                      <input
                        style={inputStyle}
                        value={item.linkLabel || ""}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.infoCards];
                            next[idx] = {
                              ...next[idx],
                              linkLabel: e.target.value,
                            };
                            return { ...prev, infoCards: next };
                          })
                        }
                      />
                    </Row>
                    <Row label="Link Path">
                      <input
                        style={inputStyle}
                        value={item.linkPath || ""}
                        onChange={(e) =>
                          updatePendantListing((prev) => {
                            const next = [...prev.infoCards];
                            next[idx] = {
                              ...next[idx],
                              linkPath: e.target.value,
                            };
                            return { ...prev, infoCards: next };
                          })
                        }
                      />
                    </Row>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantListing((prev) => ({
                        ...prev,
                        infoCards: prev.infoCards.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Card
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <Row label="FAQ Title">
                <input
                  style={inputStyle}
                  value={pendantListing.faqTitle}
                  onChange={(e) =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      faqTitle: e.target.value,
                    }))
                  }
                />
              </Row>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>FAQ Items</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantListing((prev) => ({
                      ...prev,
                      faqItems: [
                        ...prev.faqItems,
                        { question: "", answer: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add FAQ
                </button>
              </div>
              {pendantListing.faqItems.map((item, idx) => (
                <div
                  key={`listing-faq-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <Row label="Question">
                    <input
                      style={inputStyle}
                      value={item.question}
                      onChange={(e) =>
                        updatePendantListing((prev) => {
                          const next = [...prev.faqItems];
                          next[idx] = {
                            ...next[idx],
                            question: e.target.value,
                          };
                          return { ...prev, faqItems: next };
                        })
                      }
                    />
                  </Row>
                  <Row label="Answer">
                    <textarea
                      rows={3}
                      style={textAreaStyle}
                      value={item.answer}
                      onChange={(e) =>
                        updatePendantListing((prev) => {
                          const next = [...prev.faqItems];
                          next[idx] = { ...next[idx], answer: e.target.value };
                          return { ...prev, faqItems: next };
                        })
                      }
                    />
                  </Row>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantListing((prev) => ({
                        ...prev,
                        faqItems: prev.faqItems.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove FAQ
                  </button>
                </div>
              ))}
            </div>

            <div style={INFO_PANEL_STYLE}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                Same-Day Pendant Page
              </div>
              <div style={{ color: "#64748b", fontSize: 13 }}>
                This content appears on `/same-day-pendants` and should describe
                the public fulfillment promise without hardcoded assumptions.
              </div>
            </div>

            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Eyebrow">
                <input
                  style={inputStyle}
                  value={pendantRush.eyebrow}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      eyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Title">
                <input
                  style={inputStyle}
                  value={pendantRush.title}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>

            <Row label="Hero Description">
              <textarea
                rows={4}
                style={textAreaStyle}
                value={pendantRush.description}
                onChange={(e) =>
                  updatePendantRush((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Rush Page Action Links</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      actions: [...prev.actions, { label: "", path: "" }],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Action
                </button>
              </div>
              {pendantRush.actions.map((item, idx) => (
                <div
                  key={`rush-action-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Label">
                      <input
                        style={inputStyle}
                        value={item.label}
                        onChange={(e) =>
                          updatePendantRush((prev) => {
                            const next = [...prev.actions];
                            next[idx] = { ...next[idx], label: e.target.value };
                            return { ...prev, actions: next };
                          })
                        }
                      />
                    </Row>
                    <Row label="Path">
                      <input
                        style={inputStyle}
                        value={item.path}
                        onChange={(e) =>
                          updatePendantRush((prev) => {
                            const next = [...prev.actions];
                            next[idx] = { ...next[idx], path: e.target.value };
                            return { ...prev, actions: next };
                          })
                        }
                      />
                    </Row>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantRush((prev) => ({
                        ...prev,
                        actions: prev.actions.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Action
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  Rush Service Promise Stats
                </div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      serviceStats: [
                        ...prev.serviceStats,
                        { label: "", value: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Stat
                </button>
              </div>
              {pendantRush.serviceStats.map((item, idx) => (
                <div
                  key={`rush-stat-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Label">
                      <input
                        style={inputStyle}
                        value={item.label}
                        onChange={(e) =>
                          updatePendantRush((prev) => {
                            const next = [...prev.serviceStats];
                            next[idx] = { ...next[idx], label: e.target.value };
                            return { ...prev, serviceStats: next };
                          })
                        }
                      />
                    </Row>
                    <Row label="Value">
                      <input
                        style={inputStyle}
                        value={item.value}
                        onChange={(e) =>
                          updatePendantRush((prev) => {
                            const next = [...prev.serviceStats];
                            next[idx] = { ...next[idx], value: e.target.value };
                            return { ...prev, serviceStats: next };
                          })
                        }
                      />
                    </Row>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantRush((prev) => ({
                        ...prev,
                        serviceStats: prev.serviceStats.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Stat
                  </button>
                </div>
              ))}
            </div>

            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Process Eyebrow">
                <input
                  style={inputStyle}
                  value={pendantRush.processEyebrow}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      processEyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Process Title">
                <input
                  style={inputStyle}
                  value={pendantRush.processTitle}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      processTitle: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>

            <Row label="Process Description">
              <textarea
                rows={3}
                style={textAreaStyle}
                value={pendantRush.processDescription}
                onChange={(e) =>
                  updatePendantRush((prev) => ({
                    ...prev,
                    processDescription: e.target.value,
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Rush Process Steps</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      processSteps: [
                        ...prev.processSteps,
                        { title: "", body: "", linkLabel: "", linkPath: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add Step
                </button>
              </div>
              {pendantRush.processSteps.map((item, idx) => (
                <div
                  key={`rush-step-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <Row label="Step Title">
                    <input
                      style={inputStyle}
                      value={item.title}
                      onChange={(e) =>
                        updatePendantRush((prev) => {
                          const next = [...prev.processSteps];
                          next[idx] = { ...next[idx], title: e.target.value };
                          return { ...prev, processSteps: next };
                        })
                      }
                    />
                  </Row>
                  <Row label="Step Body">
                    <textarea
                      rows={3}
                      style={textAreaStyle}
                      value={item.body}
                      onChange={(e) =>
                        updatePendantRush((prev) => {
                          const next = [...prev.processSteps];
                          next[idx] = { ...next[idx], body: e.target.value };
                          return { ...prev, processSteps: next };
                        })
                      }
                    />
                  </Row>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantRush((prev) => ({
                        ...prev,
                        processSteps: prev.processSteps.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove Step
                  </button>
                </div>
              ))}
            </div>

            <div style={RESPONSIVE_TWO_COLS}>
              <Row label="Pickup Eyebrow">
                <input
                  style={inputStyle}
                  value={pendantRush.pickupEyebrow}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      pickupEyebrow: e.target.value,
                    }))
                  }
                />
              </Row>
              <Row label="Pickup Title">
                <input
                  style={inputStyle}
                  value={pendantRush.pickupTitle}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      pickupTitle: e.target.value,
                    }))
                  }
                />
              </Row>
            </div>

            <Row label="Pickup Empty Text">
              <textarea
                rows={2}
                style={textAreaStyle}
                value={pendantRush.pickupEmptyText}
                onChange={(e) =>
                  updatePendantRush((prev) => ({
                    ...prev,
                    pickupEmptyText: e.target.value,
                  }))
                }
              />
            </Row>

            <div style={{ display: "grid", gap: 10 }}>
              <Row label="Rush FAQ Title">
                <input
                  style={inputStyle}
                  value={pendantRush.faqTitle}
                  onChange={(e) =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      faqTitle: e.target.value,
                    }))
                  }
                />
              </Row>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 700 }}>Rush FAQ Items</div>
                <button
                  type="button"
                  onClick={() =>
                    updatePendantRush((prev) => ({
                      ...prev,
                      faqItems: [
                        ...prev.faqItems,
                        { question: "", answer: "" },
                      ],
                    }))
                  }
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f8fafc",
                    color: "#111827",
                    borderRadius: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Add FAQ
                </button>
              </div>
              {pendantRush.faqItems.map((item, idx) => (
                <div
                  key={`rush-faq-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <Row label="Question">
                    <input
                      style={inputStyle}
                      value={item.question}
                      onChange={(e) =>
                        updatePendantRush((prev) => {
                          const next = [...prev.faqItems];
                          next[idx] = {
                            ...next[idx],
                            question: e.target.value,
                          };
                          return { ...prev, faqItems: next };
                        })
                      }
                    />
                  </Row>
                  <Row label="Answer">
                    <textarea
                      rows={3}
                      style={textAreaStyle}
                      value={item.answer}
                      onChange={(e) =>
                        updatePendantRush((prev) => {
                          const next = [...prev.faqItems];
                          next[idx] = { ...next[idx], answer: e.target.value };
                          return { ...prev, faqItems: next };
                        })
                      }
                    />
                  </Row>
                  <button
                    type="button"
                    onClick={() =>
                      updatePendantRush((prev) => ({
                        ...prev,
                        faqItems: prev.faqItems.filter(
                          (_row, rowIndex) => rowIndex !== idx,
                        ),
                      }))
                    }
                    style={{
                      justifySelf: "start",
                      border: "1px solid #fecaca",
                      background: "#fff1f2",
                      color: "#be123c",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Remove FAQ
                  </button>
                </div>
              ))}
            </div>
          </div>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="legal"
            title="Legal + Contact"
            description="Page copy plus structured contact details used in the footer and contact page."
            onSave={() => saveFragment("legal", getSectionFragment("legal", content))}
            saveDisabled={!isSectionDirty("legal")}
            saving={savingSection === "legal"}
            savedAt={savedAtBySection.legal}
            alternate={isAlternateSection("legal")}
            wide
          >
          <Row label="About Us" hint="HTML is supported.">
            <HtmlInput
              rows={6}
              value={content.aboutUs}
              onChange={(next) => updateField("aboutUs", next)}
            />
          </Row>
          <div style={RESPONSIVE_TWO_COLS}>
            <Row label="Contact Email" hint="Shown in footer and contact page.">
              <input
                type="email"
                style={inputStyle}
                value={content.contactInfo.email}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    contactInfo: {
                      ...prev.contactInfo,
                      email: e.target.value,
                    },
                  }))
                }
              />
            </Row>
            <Row label="Contact Phone" hint="Shown in footer and contact page.">
              <input
                style={inputStyle}
                value={content.contactInfo.phone}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    contactInfo: {
                      ...prev.contactInfo,
                      phone: e.target.value,
                    },
                  }))
                }
              />
            </Row>
          </div>
          <Row
            label="Contact Address"
            hint="Use line breaks if you want the address stacked."
          >
            <textarea
              rows={4}
              style={{ ...inputStyle, resize: "vertical" }}
              value={content.contactInfo.address}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  contactInfo: {
                    ...prev.contactInfo,
                    address: e.target.value,
                  },
                }))
              }
            />
          </Row>
          <Row label="Contact Page Intro" hint="HTML is supported.">
            <HtmlInput
              rows={6}
              value={content.contactUs}
              onChange={(next) => updateField("contactUs", next)}
            />
          </Row>
          <Row label="Terms of Service" hint="HTML is supported.">
            <HtmlInput
              rows={8}
              value={content.termsOfService}
              onChange={(next) => updateField("termsOfService", next)}
            />
          </Row>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="faq"
            title="FAQ"
            description="Questions and answers shown on the public FAQ page."
            onSave={() => saveFragment("faq", getSectionFragment("faq", content))}
            saveDisabled={!isSectionDirty("faq")}
            saving={savingSection === "faq"}
            savedAt={savedAtBySection.faq}
            alternate={isAlternateSection("faq")}
          >
          <div style={{ display: "grid", gap: 8 }}>
            {faq.map((item, idx) => (
              <div
                key={`faq-${idx}`}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  display: "grid",
                  gap: 8,
                }}
              >
                <HtmlInput
                  rows={2}
                  value={item.question}
                  onChange={(nextQuestion) => {
                    const next = [...faq];
                    next[idx] = { ...next[idx], question: nextQuestion };
                    updateField("faq", next);
                  }}
                />
                <HtmlInput
                  rows={4}
                  value={item.answer}
                  onChange={(nextAnswer) => {
                    const next = [...faq];
                    next[idx] = { ...next[idx], answer: nextAnswer };
                    updateField("faq", next);
                  }}
                />
                <button
                  type="button"
                  onClick={() =>
                    updateField(
                      "faq",
                      faq.filter((_v, i) => i !== idx),
                    )
                  }
                  style={{
                    justifySelf: "start",
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    color: "#be123c",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              updateField("faq", [...faq, { question: "", answer: "" }])
            }
            style={{
              border: "1px solid #d1d5db",
              background: "#f8fafc",
              color: "#111827",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            Add FAQ
          </button>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="social"
            title="Social + Routes"
            description="Public social links and the routes used in footer/legal navigation."
            onSave={() => saveFragment("social", getSectionFragment("social", content))}
            saveDisabled={!isSectionDirty("social")}
            saving={savingSection === "social"}
            savedAt={savedAtBySection.social}
            alternate={isAlternateSection("social")}
          >
          <Row label="Facebook URL">
            <input
              style={inputStyle}
              value={content.socialLinks.facebook}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  socialLinks: {
                    ...prev.socialLinks,
                    facebook: e.target.value,
                  },
                }))
              }
            />
          </Row>
          <Row label="Instagram URL">
            <input
              style={inputStyle}
              value={content.socialLinks.instagram}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  socialLinks: {
                    ...prev.socialLinks,
                    instagram: e.target.value,
                  },
                }))
              }
            />
          </Row>
          <Row label="TikTok URL">
            <input
              style={inputStyle}
              value={content.socialLinks.tiktok}
              onChange={(e) =>
                setContent((prev) => ({
                  ...prev,
                  socialLinks: { ...prev.socialLinks, tiktok: e.target.value },
                }))
              }
            />
          </Row>
          <div style={RESPONSIVE_TWO_COLS}>
            <Row label="About Path">
              <input
                style={inputStyle}
                value={content.legalLinks.aboutPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    legalLinks: {
                      ...prev.legalLinks,
                      aboutPath: e.target.value,
                    },
                  }))
                }
              />
            </Row>
            <Row label="FAQ Path">
              <input
                style={inputStyle}
                value={content.legalLinks.faqPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    legalLinks: { ...prev.legalLinks, faqPath: e.target.value },
                  }))
                }
              />
            </Row>
          </div>
          <div style={RESPONSIVE_TWO_COLS}>
            <Row label="Terms Path">
              <input
                style={inputStyle}
                value={content.legalLinks.termsPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    legalLinks: {
                      ...prev.legalLinks,
                      termsPath: e.target.value,
                    },
                  }))
                }
              />
            </Row>
            <Row label="Contact Path">
              <input
                style={inputStyle}
                value={content.legalLinks.contactPath}
                onChange={(e) =>
                  setContent((prev) => ({
                    ...prev,
                    legalLinks: {
                      ...prev.legalLinks,
                      contactPath: e.target.value,
                    },
                  }))
                }
              />
            </Row>
          </div>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="customize-nav"
            title="Customize Navigation"
            description="Homepage and navbar customize links."
            onSave={() =>
              saveFragment("customize-nav", getSectionFragment("customize-nav", content))
            }
            saveDisabled={!isSectionDirty("customize-nav")}
            saving={savingSection === "customize-nav"}
            savedAt={savedAtBySection["customize-nav"]}
            alternate={isAlternateSection("customize-nav")}
            wide
          >
          <div style={{ display: "grid", gap: 8 }}>
            {navGroups.map((group, idx) => (
              <div
                key={`nav-${idx}`}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <HtmlInput
                  rows={2}
                  value={group.label}
                  onChange={(nextLabel) => {
                    const next = [...navGroups];
                    next[idx] = { ...next[idx], label: nextLabel };
                    updateField("navCustomizationGroups", next);
                  }}
                />
                <input
                  style={inputStyle}
                  value={group.path}
                  onChange={(e) => {
                    const next = [...navGroups];
                    next[idx] = { ...next[idx], path: e.target.value };
                    updateField("navCustomizationGroups", next);
                  }}
                  placeholder="/customize/..."
                />
                <button
                  type="button"
                  onClick={() =>
                    updateField(
                      "navCustomizationGroups",
                      navGroups.filter((_v, i) => i !== idx),
                    )
                  }
                  style={{
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    color: "#be123c",
                    borderRadius: 8,
                    padding: "8px 10px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              updateField("navCustomizationGroups", [
                ...navGroups,
                { label: "New Type", path: "/customize/new-type" },
              ])
            }
            style={{
              border: "1px solid #d1d5db",
              background: "#f8fafc",
              color: "#111827",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            Add Customize Group
          </button>
          </SectionCard>
        ) : null}

        {mode === "content" ? (
          <SectionCard
            id="gallery"
            title="Gallery Images"
            description="Entries for `/gallery` and the homepage gallery carousel."
            onSave={() => saveFragment("gallery", getSectionFragment("gallery", content))}
            saveDisabled={!isSectionDirty("gallery")}
            saving={savingSection === "gallery"}
            savedAt={savedAtBySection.gallery}
            alternate={isAlternateSection("gallery")}
            wide
          >
          {galleryUploadError ? (
            <div
              style={{
                background: "#fff1f2",
                border: "1px solid #fecdd3",
                color: "#9f1239",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 12,
              }}
            >
              {galleryUploadError}
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10 }}>
            {galleryImages.map((image, idx) => (
              <div
                key={`gallery-${idx}`}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  display: "grid",
                  gap: 8,
                }}
              >
                <Row label="Image URL">
                  <input
                    style={inputStyle}
                    value={image.imageUrl}
                    onChange={(e) => {
                      const next = [...galleryImages];
                      next[idx] = { ...next[idx], imageUrl: e.target.value };
                      updateField("galleryImages", next);
                    }}
                  />
                </Row>
                <ImagePreview
                  label={`Gallery Preview ${idx + 1}`}
                  url={image.imageUrl}
                  height={240}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      padding: "7px 10px",
                      background: "#f8fafc",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {galleryUploadingIndex === idx
                      ? "Uploading..."
                      : "Upload Image"}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      disabled={galleryUploadingIndex === idx}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        uploadGalleryImage(idx, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {image.imageUrl ? (
                    <a
                      href={assetUrl(image.imageUrl) || image.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12,
                        color: "#2563eb",
                        textDecoration: "underline",
                      }}
                    >
                      Open image
                    </a>
                  ) : null}
                  {image.imageUrl ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!confirmAssetRemoval(`gallery image #${idx + 1}`))
                          return;
                        const next = [...galleryImages];
                        next[idx] = { ...next[idx], imageUrl: "" };
                        updateField("galleryImages", next);
                      }}
                      style={{
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "7px 10px",
                        background: "#fff1f2",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#be123c",
                      }}
                    >
                      Clear Image
                    </button>
                  ) : null}
                </div>
                <Row label="Title (HTML allowed)">
                  <HtmlInput
                    rows={2}
                    value={image.title || ""}
                    onChange={(nextTitle) => {
                      const next = [...galleryImages];
                      next[idx] = { ...next[idx], title: nextTitle };
                      updateField("galleryImages", next);
                    }}
                  />
                </Row>
                <Row label="Description (HTML allowed)">
                  <HtmlInput
                    rows={3}
                    value={image.description || ""}
                    onChange={(nextDescription) => {
                      const next = [...galleryImages];
                      next[idx] = {
                        ...next[idx],
                        description: nextDescription,
                      };
                      updateField("galleryImages", next);
                    }}
                  />
                </Row>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!image.showOnHome}
                    onChange={(e) => {
                      const next = [...galleryImages];
                      next[idx] = {
                        ...next[idx],
                        showOnHome: e.target.checked,
                      };
                      updateField("galleryImages", next);
                    }}
                  />
                  Show on Home Carousel
                </label>
                <button
                  type="button"
                  onClick={() =>
                    updateField(
                      "galleryImages",
                      galleryImages.filter((_v, i) => i !== idx),
                    )
                  }
                  style={{
                    justifySelf: "start",
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    color: "#be123c",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Remove Image
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() =>
              updateField("galleryImages", [
                ...galleryImages,
                { imageUrl: "", title: "", description: "", showOnHome: false },
              ])
            }
            style={{
              border: "1px solid #d1d5db",
              background: "#f8fafc",
              color: "#111827",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            Add Gallery Image
          </button>
          </SectionCard>
        ) : null}

        {mode === "operations" ? (
          <SectionCard
            id="pickup"
            title="Pickup Locations + Availability"
            description="Set one pickup time range for each day and block out holidays when pickup should be unavailable."
            onSave={() => saveFragment("pickup", getSectionFragment("pickup", content))}
            saveDisabled={!isSectionDirty("pickup")}
            saving={savingSection === "pickup"}
            savedAt={savedAtBySection.pickup}
            alternate={isAlternateSection("pickup")}
            wide
          >
          <div style={{ display: "grid", gap: 14 }}>
            <div style={PICKUP_CARD_SECTION_STYLE}>
              <Row
                label="Minimum pickup lead time"
                hint="Customers can only choose same-day pickup windows that start after this many hours from checkout. Default is 1 hour."
              >
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={0.25}
                  style={{ ...inputStyle, maxWidth: 220 }}
                  value={Number(content.pickupLeadTimeHours ?? 1)}
                  onChange={(e) =>
                    updateField(
                      "pickupLeadTimeHours",
                      Math.min(
                        24,
                        Math.max(0, Number(e.target.value || 0)),
                      ),
                    )
                  }
                />
              </Row>
            </div>
            {pickupLocations.map((location, idx) => {
              const holidayDates = normalizeIsoDateList(location.holidayDates);
              const weekdayOverrides = normalizeWeekdayTimeSlots(
                location.weekdayTimeSlots,
              );
              const recurringWeekdays = new Set(
                location.alwaysAvailable
                  ? WEEKDAY_OPTIONS.map((day) => day.value).filter(
                      (weekday) =>
                        getPickupWeekdaySlots(location, weekday).length > 0,
                    )
                  : sanitizeWeekdayList(location.availableWeekdays),
              );

              return (
                <div
                  key={`pickup-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    padding: 12,
                    display: "grid",
                    gap: 12,
                    background: "#fcfdff",
                  }}
                >
                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Location ID">
                      <input
                        style={inputStyle}
                        value={location.id}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            id: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Label">
                      <input
                        style={inputStyle}
                        value={location.label}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            label: e.target.value,
                          }))
                        }
                      />
                    </Row>
                  </div>

                  <div style={RESPONSIVE_THREE_COLS}>
                    <Row label="Mall Name">
                      <input
                        style={inputStyle}
                        value={location.mallName || ""}
                        placeholder="Mall of Louisiana"
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            mallName: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Store Name">
                      <input
                        style={inputStyle}
                        value={location.storeName || ""}
                        placeholder="Beyond Pendants"
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            storeName: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Pickup Description">
                      <input
                        style={inputStyle}
                        value={location.description || ""}
                        placeholder="In front of Victoria Secret"
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            description: e.target.value,
                          }))
                        }
                      />
                    </Row>
                  </div>

                  <div style={RESPONSIVE_TWO_COLS}>
                    <Row label="Address Line 1">
                      <input
                        style={inputStyle}
                        value={location.line1}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            line1: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Address Line 2">
                      <input
                        style={inputStyle}
                        value={location.line2 || ""}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            line2: e.target.value,
                          }))
                        }
                      />
                    </Row>
                  </div>

                  <div style={RESPONSIVE_THREE_COLS}>
                    <Row label="City">
                      <input
                        style={inputStyle}
                        value={location.city}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            city: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="State">
                      <input
                        style={inputStyle}
                        value={location.state || ""}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            state: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Postal Code">
                      <input
                        style={inputStyle}
                        value={location.postalCode}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            postalCode: e.target.value,
                          }))
                        }
                      />
                    </Row>
                    <Row label="Country">
                      <input
                        style={inputStyle}
                        value={location.country}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            country: e.target.value,
                          }))
                        }
                      />
                    </Row>
                  </div>

                  <Row label="Notes (HTML allowed)">
                    <HtmlInput
                      rows={2}
                      value={location.notes || ""}
                      onChange={(nextNotes) =>
                        updatePickupLocation(idx, (current) => ({
                          ...current,
                          notes: nextNotes,
                        }))
                      }
                    />
                  </Row>

                  <div
                    style={{
                      ...PICKUP_CARD_SECTION_STYLE,
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "grid", gap: 4 }}>
                      <div style={{ fontWeight: 700 }}>
                        Weekly Pickup Schedule
                      </div>
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        Turn days on, set the time window for each day, and use
                        holidays below to block specific dates.
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() =>
                          updatePickupLocation(idx, (current) =>
                            applyPickupPreset(current, [0, 1, 2, 3, 4, 5, 6]),
                          )
                        }
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "7px 12px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        All Days
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updatePickupLocation(idx, (current) =>
                            applyPickupPreset(current, [1, 2, 3, 4, 5]),
                          )
                        }
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "7px 12px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Weekdays
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updatePickupLocation(idx, (current) =>
                            applyPickupPreset(current, []),
                          )
                        }
                        style={{
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#111827",
                          borderRadius: 999,
                          padding: "7px 12px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        Clear All
                      </button>
                      <div
                        style={{
                          marginLeft: "auto",
                          color: "#475569",
                          fontSize: 12,
                          fontWeight: 600,
                          paddingTop: 7,
                        }}
                      >
                        {recurringWeekdays.size} day
                        {recurringWeekdays.size === 1 ? "" : "s"} active
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      {WEEKDAY_OPTIONS.map((day) => {
                        const recurringEnabled = recurringWeekdays.has(
                          day.value,
                        );
                        const customSlots =
                          weekdayOverrides.find(
                            (row) => row.weekday === day.value,
                          )?.timeSlots || [];
                        const effectiveSlots = getPickupWeekdaySlots(
                          location,
                          day.value,
                        );
                        const summarySlots = customSlots.length
                          ? customSlots
                          : effectiveSlots;

                        return (
                          <div
                            key={`${location.id || idx}-weekday-${day.value}`}
                            style={{
                              ...PICKUP_WEEKDAY_ROW_STYLE,
                              gridTemplateColumns: isCompact
                                ? "minmax(0, 1fr)"
                                : PICKUP_WEEKDAY_ROW_STYLE.gridTemplateColumns,
                              background: recurringEnabled
                                ? "#f8fbff"
                                : "#ffffff",
                            }}
                          >
                            <div style={{ display: "grid", gap: 8 }}>
                              <label
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                  fontWeight: 700,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={recurringEnabled}
                                  onChange={(e) =>
                                    updatePickupLocation(idx, (current) => {
                                      const nextWeekdays = new Set(
                                        sanitizeWeekdayList(
                                          current.availableWeekdays,
                                        ),
                                      );
                                      const otherRows =
                                        normalizeWeekdayTimeSlots(
                                          current.weekdayTimeSlots,
                                        ).filter(
                                          (row) => row.weekday !== day.value,
                                        );

                                      if (e.target.checked) {
                                        const seedSlots = getPickupWeekdaySlots(
                                          current,
                                          day.value,
                                        );
                                        nextWeekdays.add(day.value);
                                        otherRows.push({
                                          weekday: day.value,
                                          timeSlots: seedSlots.length
                                            ? seedSlots
                                            : getPickupScheduleSeedSlots(
                                                current,
                                              ),
                                        });
                                      } else {
                                        nextWeekdays.delete(day.value);
                                      }

                                      return {
                                        ...current,
                                        alwaysAvailable: false,
                                        availableWeekdays:
                                          Array.from(nextWeekdays),
                                        weekdayTimeSlots: otherRows,
                                        timeSlots: [],
                                      };
                                    })
                                  }
                                />
                                {day.label}
                              </label>
                              <div
                                style={{
                                  color: "#64748b",
                                  fontSize: 12,
                                  lineHeight: 1.5,
                                }}
                              >
                                {recurringEnabled
                                  ? summarySlots.length
                                    ? summarySlots[0]
                                    : "Enabled but no time windows yet."
                                  : "Closed"}
                              </div>
                            </div>

                            <SingleTimeRangeEditor
                              slot={customSlots[0] || ""}
                              disabled={!recurringEnabled}
                              onChange={(nextSlots) =>
                                updatePickupLocation(idx, (current) => {
                                  const otherRows = normalizeWeekdayTimeSlots(
                                    current.weekdayTimeSlots,
                                  ).filter((row) => row.weekday !== day.value);
                                  if (nextSlots.length) {
                                    otherRows.push({
                                      weekday: day.value,
                                      timeSlots: nextSlots,
                                    });
                                  }
                                  return {
                                    ...current,
                                    alwaysAvailable: false,
                                    timeSlots: [],
                                    weekdayTimeSlots: otherRows,
                                  };
                                })
                              }
                              emptyLabel={
                                recurringEnabled
                                  ? "Set the start and end time for this day."
                                  : "Turn this day on to set pickup hours."
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(260px, 1fr))",
                      gap: 12,
                      alignItems: "start",
                    }}
                  >
                    <div style={PICKUP_CARD_SECTION_STYLE}>
                      <Row
                        label="Holiday Blackout Dates"
                        hint="Type one date per line in YYYY-MM-DD format. Do not use commas. These dates stay unavailable even if the weekday is active."
                      >
                        <textarea
                          style={{ ...textAreaStyle, minHeight: 150 }}
                          placeholder={"2026-12-25\n2026-01-01"}
                          value={formatIsoDateTextarea(holidayDates)}
                          onChange={(e) =>
                            updatePickupLocation(idx, (current) => ({
                              ...current,
                              holidayDates: parseIsoDateTextarea(
                                e.target.value,
                              ),
                            }))
                          }
                        />
                      </Row>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!location.enabled}
                        onChange={(e) =>
                          updatePickupLocation(idx, (current) => ({
                            ...current,
                            enabled: e.target.checked,
                          }))
                        }
                      />
                      Enabled
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        updateField(
                          "pickupLocations",
                          pickupLocations.filter((_v, i) => i !== idx),
                        )
                      }
                      style={{
                        border: "1px solid #fecaca",
                        background: "#fff1f2",
                        color: "#be123c",
                        borderRadius: 8,
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Remove Location
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() =>
              updateField("pickupLocations", [
                ...pickupLocations,
                {
                  id: `pickup-${pickupLocations.length + 1}`,
                  label: "",
                  mallName: "",
                  storeName: "",
                  description: "",
                  line1: "",
                  line2: "",
                  city: "",
                  state: "",
                  postalCode: "",
                  country: "US",
                  notes: "",
                  enabled: true,
                  alwaysAvailable: false,
                  availableDates: [],
                  holidayDates: [],
                  availableWeekdays: [0, 1, 2, 3, 4, 5, 6],
                  timeSlots: [],
                  weekdayTimeSlots: WEEKDAY_OPTIONS.map((day) => ({
                    weekday: day.value,
                    timeSlots: [DEFAULT_TIME_SLOT],
                  })),
                },
              ])
            }
            style={{
              border: "1px solid #d1d5db",
              background: "#f8fafc",
              color: "#111827",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
              justifySelf: "start",
            }}
          >
            Add Pickup Location
          </button>
          </SectionCard>
        ) : null}
      </div>
    </div>
  );
}

export function OperationsSettingsPage() {
  return <SiteSettingsPage mode="operations" />;
}

export default function ContentManagementPage() {
  return <SiteSettingsPage mode="content" />;
}
